import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, ViewChild } from '@angular/core';
import { LL } from 'i18n/i18n.service';
import { TranslatedString, untranslatedString } from 'models/translated-string';
import { FoodDbService, FoodLikedChangedEvent, FOOD_DB_READY_TIMEOUT_MS } from 'services/food-db.service';
import { FoodReadFailurePolicy } from 'services/food-read-error';
import { AuthService } from 'services/auth.service';
import { SearchConnectionError, ReportedSearchFailureError, isPassingSearchReadFailure } from 'services/food-search-error';
import { TypoTolerance } from '@cf-shared/food-search/food-search-query';
import { DefaultFoodsUnavailableError } from 'services/food-search-default-results';
import { describeError, describeErrorForLog } from '@cf-shared/error-describe';
import { describeRowWithoutId, splitRowsByUsableId } from 'services/food-search-rows-without-id';
import { isEmpty } from 'lodash-es';
import { ImageService } from 'services/image-service';
import { UserDb } from 'services/user-db.service';
import { ScaledImage } from '@cf-shared/data-model/scaled-image';
import { PopupMenuProvider, PopupMenuButton } from 'providers/popup-menu/popup-menu';
import { DebugService } from 'services/debug.service';
import { sleep } from '@cf-shared/shared-util';
import { CsPage, NavController } from 'providers/nav-controller';
import { TelemetryService } from 'services/telemetry.service';
import { FormatNutrients } from 'util/formatting';
import { SearchOperationTracker, TrackedSearchOperation } from 'util/search-operation-tracker';
import { LoggingService } from 'services/logging.service';
import { ToastController } from 'providers/toast-controller';
import { PubSubRegistration, PubSubService } from 'services/pubsub.service';
import { FoodUpdatedEvent, DocWatcher } from 'services/database-wrapper';
import { FoodHeader, FoodStatus, foodStatusIcons, foodTypeIcons, FoodType, isLogMeal, isLogMealById, hasFoodBeenLoggedBefore, canForgetFoodFromHistory, FOOD_FORGOTTEN_LAST_USED_UTC, getFoodSource, foodSourceInfoTable, Food, FoodMeasure, Nutrients } from '@cf-shared/data-model/food';
import { Log_Meal } from '@cf-shared/data-model/log-meal';
import { isRecipeById, isRecipe } from '@cf-shared/data-model/recipe';
import { SettingsService, FoodValuesDisplayMode } from 'services/settings.service';
import { IEditMealPage } from 'pages/edit-meal/edit-meal-interface';
import { FoodDetailsExit, FoodDetailsResult, IEditFoodPage } from 'pages/edit-food/edit-food-interface';
import { buildRecipeServingMeasure } from 'pages/edit-food/recipe-measures';
import { describeFoodCardNutrition } from './food-card-nutrition';
import { IBarcodeScannerPage } from 'pages/barcode-scanner-page/barcode-scanner-interface';
import { ILabelScannerPage } from 'pages/label-scanner-page/label-scanner-interface';
import { ConfigDbService } from 'services/config-db.service';
import { ConfigKey } from 'services/config-keys';
import { StaplePictureFiles } from 'services/staple-picture-files.generated';
import { FoodWithMaybePicture, staplePictureFor } from 'services/staple-picture-lookup';
import { getPageName } from 'app/app-page-helpers';
import { IEditIngredientPage } from 'pages/edit-ingredient/edit-ingredient-interface';
import { buildLogPortionScreenParams } from 'pages/edit-ingredient/log-mode-open';
import { AddFoodErrand, ErrandAnswerer, ErrandAnswerShape, answerAddFoodErrandAsync, hasAddFoodIntent, readAddFoodErrand } from './add-food-errand';
import { IngredientMacro, IngredientType } from '@cf-shared/data-model/ingredient';
import { UiDisplayNutrient, uiDisplayNutrients } from 'models/display-nutrient';
import {
  FoodsListMessage, FoodsListState, FoodsSearchCard, ServerSearchOutcome,
  pickFoodsListMessage, pickFoodsSearchCard,
} from './foods-list-message';
import {
  DEFAULT_FOODS_FILTER_STATE, FILTER_TELEMETRY_NAMES, FoodFilterChip, FoodFilterName, FoodTypeFilter,
  FoodsFilterState, activeFilterCount, applyFoodFilters, buildChipLine, combineFilterState,
  restoreRemembered, serializeRemembered,
} from './foods-filters';
import { OnlineStateService } from 'services/online-state.service';
import { rankFoodHeaders } from '@cf-shared/food-search/food-search-ranking';
import { FoodWordCompleter } from '@cf-shared/food-search/food-word-completer';
import { withOfflineStaples } from './staple-offline-search';
import { moveStapleToFront, serverStapleRow, StapleResultRow } from './staple-result-row';
import { PreparedStapleWords, stapleDeviceRow } from 'services/staple-db-snapshot';
import { StapleDbService } from 'services/staple-db.service';
import { StapleNames, StapleSuggester } from 'services/staple-suggestions';
import {
  FoodSuggestController, SuggestionList, looksLikeBarcode,
} from 'services/food-suggest-client';
import { SuggestionRow } from '@cf-shared/food-search/food-suggestion-row';
import { FoodSuggestionRows, SuggestionKeyActionKind } from 'services/food-suggestion-rows';
import { NO_SEARCH_CORRECTION, SearchCorrection, SearchCorrectionKind } from 'services/food-search-correction';
import { CsInputComponent } from 'components/cs-input/cs-input';
import { BadgePosition } from 'components/cs-icon-button/cs-icon-button';

interface FoodForUI extends FoodHeader {
  image?: ScaledImage;
  sourceShortName?: string;
  nutrientsPer100g?: Nutrients;
  measuresLoading?: boolean;
  /** #4541: set when this food's stated calories cannot match its own macros. Worked out
   * once, where nutrientsPer100g is filled in, so the list does not recompute it on every
   * change-detection pass. */
  inconsistentNutrientsInfo?: TranslatedString;
  /** true when the full food has no calories and no macros at all, or is a recipe with no
   * usable total weight. Set in the same places as inconsistentNutrientsInfo; see
   * describeFoodCardNutrition. */
  noNutritionData?: boolean;
}

/**
 * Copies everything a card shows about a fully loaded food onto its list entry.
 *
 * There are several places that learn the full food behind a row after the row is already on
 * screen — the detail load, the background fetch for rows that arrived as headers, and the
 * freshness refresh — and each one has to put the same things on the row. Keeping that in one
 * function is what stops a fourth card field being added to two of them and forgotten in the
 * third.
 */
function applyLoadedFoodToRow(uiFood: FoodForUI, food: Food) {
  uiFood.cachedFood = food;
  uiFood.image = food.image;
  applyCardNutrition(uiFood, food);
}

/** copies what the card needs about a fully loaded food's nutrition onto its list entry */
function applyCardNutrition(uiFood: FoodForUI, food: Food) {
  const nutrition = describeFoodCardNutrition(food);
  uiFood.nutrientsPer100g = nutrition.nutrientsPer100g;
  uiFood.inconsistentNutrientsInfo = nutrition.inconsistentNutrientsInfo;
  uiFood.noNutritionData = nutrition.noNutritionData;
}

/** how long a search waits for a picked staple read by id before listing without it (food staples plan 4b) */
const PICKED_STAPLE_READ_LIMIT_MS = 3000;

interface SearchOperation extends TrackedSearchOperation {
  isBarcodeSearch: boolean;
  allHeaders: FoodHeader[];
  loadingFoods: FoodForUI[];
  /** set once this search has put its results on screen — see the catch in
   *  searchFoodsAsync_internal for why a later failure must not undo that (#4100). */
  published?: boolean;
}

import { FoodsPageParams, FoodsPageResult } from './foods-interface';

@Component({
  selector: 'page-foods',
  templateUrl: 'foods.html',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FoodsPage extends CsPage<FoodsPageParams> {
  protected LL = LL;

  protected showSpinner = false;
  protected searchText = '';

  /**
   * The last word of a suggestion the user picked, while it is still the last word in the box.
   *
   * Picking a line means "I meant THIS", so while it stands its last word is not finished off
   * again when the search is ranked -- otherwise picking "chicken breast" would rank as if
   * "breasts" had been typed too. Typing anything clears it.
   */
  private pickedCompletion: string | undefined = undefined;
  /**
   * The whole line the person picked from the suggestions, trimmed (#5579). A search for exactly
   * this text is sent without typo tolerance: the line came from real food names, so the tolerance
   * would only add near misses. Typing anything clears it.
   */
  private pickedLine: string | undefined = undefined;
  /** the staple row picked with that line (food staples plan 4b); its food is shown first */
  private pickedStapleId: string | undefined = undefined;

  /** finishes off a part-typed word for the suggestion rows and for ranking (df-d2e69a0d16ea) */
  private readonly wordCompleter: FoodWordCompleter;
  /** matches typed text against the staple rows; rebuilt whenever those rows change */
  private stapleSuggester: StapleSuggester | undefined = undefined;
  /** the rows the current suggester was built from, so a corrected staple rebuilds it */
  private stapleSuggesterRows: StapleNames[] | undefined = undefined;
  /** the staple rows the last warm-up read; undefined until the device's copy is ready */
  private warmedStapleRows: StapleNames[] | undefined = undefined;
  /**
   * Staple picture files whose load failed on this page, by url. Kept so a row that already showed a
   * broken picture falls back to the shield rather than trying the same file on every redraw.
   */
  private readonly staplePicturesThatFailedToLoad = new Set<string>();
  // the listed images whose thumbnail could not load. kept beside the food, never on it: the
  // listed food is the user's own object, and clearing its image risked saving the loss (#5692).
  // a reload of the list brings fresh objects, which get another try.
  private readonly imagesThatFailedToLoad = new WeakSet<ScaledImage>();
  /** the same staples as search headers, for a search the server could not answer */
  private warmedStapleHeaders: FoodHeader[] | undefined = undefined;
  /** those staples' names and aliases already split and stemmed, so the ranking does not redo it */
  private preparedStapleWords: ReadonlyMap<string, PreparedStapleWords> | undefined = undefined;

  /**
   * the suggestion rows under the search box (#5410): what is shown, the highlight, the keys and
   * their analytics events; see food-suggestion-rows.ts (df-5a63574c1598)
   */
  protected readonly suggestionRows: FoodSuggestionRows;
  /**
   * The text whose results are on screen. Typing does not search (#5410), so the box can hold
   * words nobody has searched for yet, and anything that means "the search the user is looking
   * at" -- the barcode that found nothing, the text a pick or a dismissal is reported against --
   * reads this instead of the box.
   */
  private listedSearchText = '';
  /**
   * The listed search whose dismissal has already been reported. Clearing the
   * box reports the search behind the list, but that search stays listed until a new one runs, so
   * without this a second clear, or leaving the page, would report the same dismissal again.
   * Reset whenever a new search is listed.
   */
  private dismissalReportedFor: string | undefined = undefined;

  /**
   * What is being said about the spelling of the search on screen (#5410): nothing, an offer, or
   * a correction that was run instead of what was typed. The template reads it to draw the
   * spelling line (or lines) above the results.
   */
  protected correction: SearchCorrection = NO_SEARCH_CORRECTION;
  /** what the person actually typed, kept so an applied correction offers a way back to it */
  protected correctionTypedText = '';
  /**
   * A search nothing should be said about, because the person chose it themselves at the
   * spelling line -- by taking the correction, or by going back to their own spelling. Without
   * it, either choice would immediately be second-guessed by another correction.
   */
  private searchChosenAtTheCard = '';
  /** the commit a correction lookup belongs to; a newer commit makes an older answer stale */
  private correctionGeneration = 0;
  /** for the template */
  protected readonly SearchCorrectionKind = SearchCorrectionKind;

  /** turns keystrokes into suggestion lists; see food-suggest-client.ts */
  private suggestController: FoodSuggestController;
  /** the search box as a component, so a commit can close the phone's keyboard */
  @ViewChild('searchInput', { read: CsInputComponent, static: true }) private searchInputComponent: CsInputComponent;
  /** has `ionViewDidEnter` run on this page object before? Decides the autofocus; see that hook. */
  private hasEnteredBefore = false;
  public searchIsBarcode = false;
  // how the last search's SERVER-side request ended. Drives the honest "couldn't load —
  // connection problem" empty state with a Retry (#3743) and the card above the list that says
  // the results are local-only (#5114). See ServerSearchOutcome for why it is not a boolean.
  protected serverSearch = ServerSearchOutcome.Contributed;
  // true when the search pass itself failed, so there is no list to show and we must say so
  // rather than leave a spinner running forever (#4100). Distinct from `serverSearch`, which is
  // specifically about the SERVER search — this one also covers the read of the user's own
  // foods failing, which is what an empty search box depends on.
  protected searchFailed = false;
  protected searchDebouncerTimeout: ReturnType<typeof setTimeout> = undefined;
  protected mode: 'select' | 'list' | '' = '';
  protected FoodStatus = FoodStatus;

  /** the types the Type picker offers, in order */
  protected readonly typeFilterList = [
    FoodTypeFilter.All, FoodTypeFilter.Foods, FoodTypeFilter.Recipes, FoodTypeFilter.Meals,
  ];
  /** for the template */
  protected readonly FoodTypeFilter = FoodTypeFilter;
  protected readonly BadgePosition = BadgePosition;

  /**
   * The filters come from three layers, combined by `combineFilterState` (#5549):
   * what the user chose and this device remembers, what a route set for this visit, and what a
   * barcode search set until the next ordinary search. Only the first is ever written to storage.
   */
  private rememberedFilters: FoodsFilterState = { ...DEFAULT_FOODS_FILTER_STATE };
  /** what a route such as "View All Recent Meals" set; every field, so the visit starts from defaults */
  private routeFilters: Partial<FoodsFilterState> = {};
  /** what a barcode search set, dropped at the next search that is not a barcode */
  private scanFilters: Partial<FoodsFilterState> = {};
  /** the barcode `scanFilters` belongs to, so searching it again does not overrule a later tap */
  private scanFilterBarcode: string | undefined = undefined;
  /**
   * Have the remembered filters been read, and any route filter applied, for THIS page object?
   * `ionViewCanEnter` runs again every time the user comes back from the portion screen, and
   * re-reading there would undo a filter they removed while they were choosing an amount.
   */
  private filtersApplied = false;

  /** what the page actually filters and draws by */
  protected get filters(): FoodsFilterState {
    return combineFilterState(this.rememberedFilters, this.routeFilters, this.scanFilters);
  }

  /** how many filters are not at their default; drawn on the Filters icon */
  protected get activeFilterCount(): number {
    return activeFilterCount(this.filters);
  }

  /** the chips after the type chip: what is on, then the fixed My Foods and Favorites when off */
  protected get filterChips(): FoodFilterChip[] {
    return buildChipLine(this.filters);
  }

  protected foods: FoodForUI[] = [];
  // built from food/meal names (database content), not translatable catalog text
  protected foodDescriptions: Map<string, TranslatedString> = new Map();
  protected isEmpty = (elem: any) => isEmpty(elem);
  protected foodStatusIcons = foodStatusIcons;
  protected foodTypeIcons = foodTypeIcons;
  protected FoodType = FoodType;
  /**
   * Did the user pick a food out of THIS search? Read only by `ionViewWillLeave`, to decide
   * whether leaving counts as dismissing the results (`foods_search_rejected`).
   *
   * Deliberately NOT cleared when the page is re-entered. Since #4967 the list stays open
   * underneath the portion screen, so coming back to it is not a fresh visit — clearing it
   * there made every successful log report as a rejected search, because the user picked while
   * the flag was set and then left after it had been reset. It is cleared when a NEW search
   * runs, which is the question the event actually asks.
   */
  protected selectionAccepted = false;

  /**
   * Is a pick already being acted on? This is the tap guard, and it is a different question
   * from `selectionAccepted` above — it must clear when the user comes back so the next pick
   * is allowed, and when a pick opened nothing so the list does not go deaf. See
   * `handleFoodClicked`.
   */
  private pickInFlight = false;

  /**
   * The search text `selectionAccepted` currently refers to. See where it is used below for
   * why a plain "reset on every search" is not enough.
   */
  private searchTextBehindSelectionAccepted: string | undefined = undefined;

  protected labelScanEnabled = false;

  /** the enum itself, so the template can name its cases */
  protected FoodsListMessage = FoodsListMessage;
  /** the enum itself, so the template can name its cases */
  protected FoodsSearchCard = FoodsSearchCard;

  /**
   * Which single message the template shows at the bottom of the food list. The rule lives in
   * `foods-list-message.ts` so that only one message can ever be picked and a unit test can
   * check that; this only feeds it the page's current state.
   */
  protected get listMessage(): FoodsListMessage {
    return pickFoodsListMessage(this.foodsListState);
  }

  /**
   * Which card the template shows ABOVE the food list — the one that says the results are only
   * foods already stored on this device (#5114). Same arrangement as `listMessage`: the rule
   * lives in `foods-list-message.ts` where a unit test can reach it, and this only feeds it the
   * page's current state.
   */
  protected get searchCard(): FoodsSearchCard {
    return pickFoodsSearchCard(this.foodsListState);
  }

  /**
   * True when the list is the default one shown before any search (the foods the user logged
   * before): the search box is empty and there is at least one food. The template then shows one
   * "Logged Before" heading instead of a clock on every card. Uses the same state as the pickers
   * above, so the heading and the "use the search bar" message can never both show.
   */
  protected get showLoggedBeforeHeading(): boolean {
    const state = this.foodsListState;
    return !state.hasSearchText && state.resultCount > 0;
  }

  /** The page state both of the pickers above read. Gathered once so they cannot disagree. */
  private get foodsListState(): FoodsListState {
    return {
      searching: this.showSpinner,
      // typing does not search, so the list and its messages belong to the last search that ran,
      // not to the text in the box
      hasSearchText: this.listedSearchText.length > 0,
      resultCount: this.foods.length,
      searchFailed: this.searchFailed,
      serverSearch: this.serverSearch,
      // read afresh each time rather than captured when the search failed: the user may have
      // walked back into signal since. It only picks the card's wording and colour, and
      // online-state.service.ts says a passive indicator is allowed to be briefly wrong.
      deviceLikelyOffline: !this.onlineState.isLikelyOnline(),
      searchIsBarcode: this.searchIsBarcode,
      labelScanEnabled: this.labelScanEnabled,
      filtersActive: this.activeFilterCount > 0,
    };
  }

  private readonly maxListLen = 50;
  private docWatcher: DocWatcher;
  foodUpdatedSub: PubSubRegistration;
  foodLikedSub: PubSubRegistration;

  constructor(
    public navCtrl: NavController,
    public foodDb: FoodDbService,
    protected userDb: UserDb,
    protected imageService: ImageService,
    private pubSub: PubSubService,
    private popupCtrl: PopupMenuProvider,
    protected debug: DebugService,
    private cd: ChangeDetectorRef,
    private telemetry: TelemetryService,
    private logging: LoggingService,
    private toastCtrl: ToastController,
    private settings: SettingsService,
    private configDb: ConfigDbService,
    private stapleDb: StapleDbService,
    private onlineState: OnlineStateService,
    private auth: AuthService,
  ) {
    super();
    this.wordCompleter = new FoodWordCompleter();
    this.suggestionRows = new FoodSuggestionRows(this.telemetry);
    // if any scanner model is configured, label scanning is available (#3783)
    const scanConfig = this.configDb.getConfigValue<string>(ConfigKey.ScanProvider);
    this.labelScanEnabled = !!scanConfig;
    this.suggestController = new FoodSuggestController({
      requestAsync: (text, size, signal) => this.foodDb.requestFoodSuggestionsAsync(text, size, signal),
      deviceRows: text => this.wordCompleter.deviceSuggestionRows(text),
      isLikelyOnline: () => this.onlineState.isLikelyOnline(),
      onList: list => this.showSuggestionList(list),
      onWaitingChange: waiting => {
        this.suggestionRows.pending = waiting;
        this.safeMarkForCheck(this.cd);
      },
      // the person still gets the word list, so this is reported without interrupting them
      reportFailure: error => this.logging.trackSilentProblem('Food suggestions failed', { error: describeError(error) }),
      stapleRows: text => this.stapleRowsFor(text),
      setTimer: (callback, ms) => this.safeTimeout(callback, ms),
      clearTimer: handle => this.cancelTimeout(handle as ReturnType<typeof setTimeout>),
    });
  }

  /** the translated name of one type, for the type chip and the Type picker */
  protected typeFilterLabel(type: FoodTypeFilter): TranslatedString {
    switch (type) {
      case FoodTypeFilter.Foods: return LL.foods.filters.foods();
      case FoodTypeFilter.Recipes: return LL.foods.filters.recipes();
      case FoodTypeFilter.Meals: return LL.foods.filters.meals();
      default: return LL.foods.filters.all();
    }
  }

  /** the translated name of one on-or-off filter, for its chip and its menu row */
  protected filterLabel(name: FoodFilterName): TranslatedString {
    switch (name) {
      case 'myFoods': return LL.foods.filters.myFoods();
      case 'favorites': return LL.foods.filters.favorites();
      case 'loggedBefore': return LL.foods.filters.loggedBefore();
      case 'hideUnverified': return LL.foods.filters.hideUnverified();
    }
  }

  /** what a screen reader reads on a chip's × button */
  protected removeFilterLabel(name: FoodFilterName): TranslatedString {
    return LL.foods.filters.removeFilter({ filter: this.filterLabel(name) });
  }

  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;

  isSearchInFocus() {
    let inputElement = this.searchInput.nativeElement as HTMLInputElement;
    if (inputElement) {
      return inputElement === document.activeElement;
    }
    return false;
  }

  ngOnInit() {
    // Add an event subscriber when  update is triggered from edit page
    this.foodUpdatedSub = this.pubSub.subscribe(FoodUpdatedEvent, () => {
      // a food was saved or deleted, so a search for the same text still running read the old
      // foods: restart rather than let it stand in for this one (df-1f667923b17d)
      this.restartSearch();
    });
    // a vote on the details screen (or anywhere else) does not re-run the search, so update the
    // like mark on the listed card directly
    this.foodLikedSub = this.pubSub.subscribe(FoodLikedChangedEvent, change => this.applyLikedChange(change.foodId, change.liked));
  }

  /** Show a changed vote on the listed card for that food, if it is listed. */
  private applyLikedChange(foodId: string, liked: -1 | 1 | undefined) {
    let changed = false;
    // change the listed object in place: the per-food detail load may still be filling these same
    // objects in (see publishing in the search), so a copy would miss that. The card takes `liked`
    // as its own input, which is what makes it redraw.
    for (const food of this.foods) {
      if (food._id === foodId && food.liked !== liked) {
        food.liked = liked;
        changed = true;
      }
    }
    if (changed) {
      this.cd.markForCheck();
    }
  }

  ngOnDestroy() {
    this.suggestController.cancel();
    this.docWatcher?.destroy();
    if (this.foodUpdatedSub) {
      this.foodUpdatedSub.unsubscribe();
    }
    this.foodLikedSub?.unsubscribe();
  }

  getFoodTypeIconForFood(food: FoodForUI) {
    if (isLogMeal(food.cachedFood)) {
      return foodTypeIcons[FoodType.LogMeal].name;
    } else if (isRecipeById(food._id)) {
      // isRecipeById now includes legacy meals - all show Recipe icon
      return foodTypeIcons[FoodType.Recipe].name;
    }
    return foodTypeIcons[FoodType.Food].name;
  }

  // Check if food is a recent/log meal (for showing "Meal" badge)
  // Legacy saved meals are now treated as recipes and show Recipe badge instead
  isLogMealEntry(food: FoodForUI): boolean {
    return !!isLogMeal(food.cachedFood);
  }

  // true when the user has actually logged/used this food before, so its card shows the
  // "Logged Before" badge. Delegates to the shared predicate (meal-exclusion + used-date).
  isFoodUsedBefore(food: FoodForUI): boolean {
    return hasFoodBeenLoggedBefore(food.cachedFood);
  }

  handleScanBarcodeButtonClick(e: Event) {
    e.stopPropagation();
    this.scanBarcode();
    this.telemetry.trackEvent("foods_barcode", {});
  }

  getLikedIcon(food: FoodForUI) {
    if (food.liked > 0) {
      return "liked";
    } else if (food.liked < 0) {
      return "disliked";
    } else {
      return "none";
    }
  }

  hasImage(food: FoodForUI) {
    return food.image && food.image.sizes && food.image.sizes.length > 0 && !this.imagesThatFailedToLoad.has(food.image);
  }

  async ionViewCanEnter() {
    // Wait for the shared food databases before touching them. A warm resume used to rebuild
    // the saved navigation stack BEFORE it started those databases, so a person who reloaded the
    // browser while this page was open ran the line below with `foodDb.database` still
    // undefined. That TypeError reached cs-nav as an unexpected crash, so it was reported as an
    // abnormal exception and the person was left on Settings under the "This couldn't be
    // opened" toast instead of seeing their foods (df-5d2fd4601fb1). Since #6790 the warm resume
    // starts the food database in Phase A, so this wait returns at once there; a page opened
    // from a link during a cold boot can still arrive first.
    //
    // Deliberately this wait and NOT `if (this.foodDb.database)`, which looks equivalent and is
    // not: that field is assigned at the top of initializeAsync, before the shared-database
    // handshake it then awaits, so testing it reports "ready" during the very window this wait
    // exists to cover. See awaitReadyWithTimeoutAsync's own comment.
    if (!await this.foodDb.awaitReadyWithTimeoutAsync(FOOD_DB_READY_TIMEOUT_MS)) {
      // Refuse the page the sanctioned way rather than letting a read blow up: this reports
      // PageLoadFailed with the reason, shows the user the same toast, and raises the refusal
      // type so cs-nav does not report it a second time as a crash.
      return this.auth.failPageGuardAsync(
        `Food database was not ready within ${FOOD_DB_READY_TIMEOUT_MS} ms while opening the foods list`);
    }

    if (!this.docWatcher) {
      this.docWatcher = this.foodDb.database.createDocWatcher((_changes) => {
        if (this.viewDestroyed) return;
        this.cd.markForCheck();
      });
    }

    this.mode = this.navParams.data.mode || '';
    if (!['select', 'list'].includes(this.mode)) {
      this.mode = 'list';
    }

    // once per page object, and only after the wait above: on a warm resume this runs before the
    // databases and the signed-in user are ready, and the remembered filters are stored per user.
    // Coming back from the portion screen runs this method again and must change nothing, or a
    // filter the user removed on the way out would come back with them (#5549).
    if (!this.filtersApplied) {
      this.filtersApplied = true;
      this.readRememberedFilters();
      this.applyRouteFilter(this.navParams.data.filter);
    }

    if (this.navParams.data.initialSearchText) {
      this.searchText = this.navParams.data.initialSearchText;
      // text put in the box for the user, not typed by them, so offer nothing for it
      this.clearSuggestions();
    }
  }

  async ionViewDidEnter() {
    // read the device's staple copy into memory before anything asks for it: the row function
    // the typing suggestions use must answer synchronously, so it cannot wait for a read of its
    // own (food staples plan 4c, #5625). Not awaited — see the method's own comment.
    void this.warmStapleSnapshotAsync();
    // Is this the FIRST time this page object has been entered? Recorded here, before anything
    // below can change its mind, and recorded on EVERY entry rather than only on the entry that
    // ends up focusing the search box. Setting it only when the box is focused would leave it
    // false after a scanning visit, and then the next entry — which is the portion screen closing
    // back onto this list — would read as the first one and raise the keyboard over the results.
    const isFirstEntry = !this.hasEnteredBefore;
    this.hasEnteredBefore = true;
    // read before the two blocks below clear them: whether this visit is here to scan something
    // decides whether the keyboard is opened, and both flags are consumed further down
    const openingScanner = !!this.navParams.data.openBarcodeScanner;
    // if a barcode search was passed, use it as the search text
    const barcodeSearch = this.navParams.data.barcodeSearch;
    if (barcodeSearch) {
      // clear the param so it doesn't re-search on every ionViewDidEnter
      this.navParams.data.barcodeSearch = undefined;
      void this.onBarcodeScannedAsync(undefined, barcodeSearch);
    } else if (!this.foods.length) {
      void this.searchFoodsAsync(this.searchText);
    }
    // the guard only, NOT `selectionAccepted` — see both fields' comments
    this.pickInFlight = false;

    // the hour the filters are remembered for counts from the last time the page was entered,
    // left or filtered, so a long stay here does not start it in the middle of the visit
    this.saveRememberedFilters();

    // if requested, automatically open barcode scanner
    if (this.navParams.data.openBarcodeScanner) {
      // clear the flag so it doesn't re-open on every ionViewDidEnter
      this.navParams.data.openBarcodeScanner = false;
      this.scanBarcode();
    }
    // There is no matching flag for the label scanner any more (#5377). Routes that scan a label
    // open the scanner directly and hand it the errand, instead of passing through this list.

    // Put the cursor in the search box on the way in, so the user can start typing without first
    // tapping it (owner, 2026-09-20). Two conditions, both about not opening the phone keyboard
    // over something the user came here to look at.
    //
    // FIRST: only on a FRESH visit (`isFirstEntry`, worked out at the top of this hook). This hook
    // runs again every time the portion screen closes back onto this list, and taking focus there
    // would raise the keyboard over the results after every single food the user logs.
    //
    // SECOND: only when this visit opens with NOTHING already chosen for the user — see
    // `routeChoseWhatToShow` below. A visit that was sent here to see something gets no autofocus
    // at all, not a deferred one: `isFirstEntry` is already false by the time the portion screen
    // comes back.
    if (isFirstEntry && !this.routeChoseWhatToShow(barcodeSearch, openingScanner)) {
      void this.focusSearchBoxAsync();
    }
  }

  /**
   * Did whoever opened this page already decide what the list should show? (owner, 2026-09-20)
   *
   * This is the question the autofocus turns on. Somebody who opened Add Foods with nothing chosen
   * came here to type, so the cursor belongs in the search box. Somebody sent here to SEE
   * something did not, and raising the phone keyboard would cover the very thing they were sent
   * to look at. Which route counts as choosing, and why each one does, is written up once in
   * `design-docs/add-foods-page.md`, "The search box takes the cursor when the page opens".
   *
   * The two scanner values are passed in rather than read here, because `ionViewDidEnter` consumes
   * both of them further down and this is asked after that.
   */
  private routeChoseWhatToShow(barcodeSearch: string | undefined, openingScanner: boolean): boolean {
    return !!this.navParams.data.filter
      || !!this.navParams.data.initialSearchText
      || !!barcodeSearch
      || openingScanner;
  }

  /**
   * Put the cursor in the search box, once (see the call in `ionViewDidEnter`).
   *
   * One tick of delay: this page is still being slid into place while that hook runs, and
   * focusing an input that the browser has not finished laying out does nothing on some devices.
   */
  private async focusSearchBoxAsync() {
    await sleep(0);
    if (this.viewDestroyed) return;
    this.searchInputComponent?.setFocus();
  }

  async ionViewWillLeave() {
    // an empty box means the dismissal was already reported when it was cleared, and it may also
    // have been reported by a clear the person then typed over
    const dismissalAlreadyReported = this.dismissalReportedFor === this.listedSearchText;
    if (this.searchText !== '' && this.listedSearchText !== '' && !this.selectionAccepted && !dismissalAlreadyReported) {
      this.telemetry.trackEvent('foods_search_rejected', { searchText: this.listedSearchText });
    }
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    // leaving with suggestions on screen and none picked is an abandoned suggestion list
    this.suggestionRows.reportAbandonedIfShown(this.searchText);
    this.clearSuggestions();
  }

  async ionViewDidLeave() {
    // this also fires when the portion screen opens on top of the list, which is deliberate: it is
    // the closest thing to "the user left" that the app can see, and re-saving costs nothing
    this.saveRememberedFilters();

    const nextPageId = this.navCtrl.getActive().id;

    // Clear mode in case user uses primary navigation to go away from nutrition page stack.
    // The portion screen must NOT clear this list (#4967). It now opens on TOP of this page
    // and Log comes straight back, so clearing here threw away the user's search and their
    // place in it every time they logged a food — the list visibly emptied and rebuilt itself
    // underneath the portion screen.
    //
    // Asked by the page's REGISTERED NAME rather than by matching `id`. `id` is a serialized
    // URL string whose exact shape is the serializer's business, and two attempts to match it
    // by eye — first as a class name, then as a URL segment — both silently matched nothing
    // and left this bug in place. `getPageName` answers from the page registry, so it cannot
    // drift from what the page actually is.
    const nextPage = this.navCtrl.getActive();
    const nextIsPortionScreen =
      getPageName(nextPage?.component?.componentType) === IEditIngredientPage.pageName;

    if (!nextIsPortionScreen && !['EditFoodPage', 'EditMealPage'].includes(nextPageId) && !nextPageId.startsWith('meal') && !nextPageId.startsWith('food')) {
      // Reload non-global foods on page leave
      this.mode = '';
      this.foods = [];
    }
  }

  /**
   * The picture a staple shows instead of the green shield, or `undefined` for the shield (staple
   * pictures plan, #5694). Called from the template while a row renders, so it must stay synchronous
   * and cheap: the lookup reads the snapshot already in memory and never starts a read.
   *
   * A picture whose file failed to load is remembered and never offered again, so the row falls back
   * to the shield instead of showing a broken image.
   */
  staplePictureForRow(row: SuggestionRow): StaplePictureFiles | undefined {
    const files = staplePictureFor(row.stapleId, this.staplePictureDeps());
    return files === undefined || this.staplePicturesThatFailedToLoad.has(files.small) ? undefined : files;
  }

  /** the picture for a food card, or `undefined` when the card looks exactly as it does today */
  staplePictureForFood(food: FoodWithMaybePicture): StaplePictureFiles | undefined {
    const files = staplePictureFor(food, this.staplePictureDeps());
    return files === undefined || this.staplePicturesThatFailedToLoad.has(files.small) ? undefined : files;
  }

  /**
   * Whether any row on screen shows a picture. Ordinary rows then get a spacer as wide as a picture,
   * so every row's name starts at the same place; without it the names of ordinary rows would sit a
   * picture's width to the left of the staple rows'.
   */
  staplePicturesShownInList(): boolean {
    return this.suggestionRows.list.rows.some(row => this.staplePictureForRow(row) !== undefined);
  }

  /**
   * A staple picture that could not be loaded: offline on the web before the service worker has it,
   * or an app older than the upload that named it. Remember it and fall back — `handleImageLoadError`
   * below cannot serve here, because it clears the food's own photo and a staple has none.
   */
  handleStaplePictureError(url: string): void {
    this.staplePicturesThatFailedToLoad.add(url);
    this.safeMarkForCheck(this.cd);
  }

  /** what the picture lookup needs */
  private staplePictureDeps() {
    return {
      pictureNameForId: (foodId: string) => this.stapleDb.pictureNameForId(foodId),
    };
  }

  // falls back to the normal icon when a thumbnail cannot load. offline every photo fails, so this
  // only hides it and never touches the food's image (#5692)
  handleImageLoadError(foodId: string) {
    const food = this.foods.find(listed => listed._id === foodId);
    if (food?.image) {
      this.imagesThatFailedToLoad.add(food.image);
      this.safeMarkForCheck(this.cd);
    }
  }

  handleSearchChanged() {
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    // if the box was just emptied, the search behind the list was dismissed. Typing does not
    // search, so that is the listed search, not the last text in the box (#5410)
    const alreadyReported = this.dismissalReportedFor === this.listedSearchText;
    if (this.searchText === '' && this.listedSearchText.length > 1 && !alreadyReported) {
      this.telemetry.trackEvent('foods_search_rejected', { searchText: this.listedSearchText });
      this.dismissalReportedFor = this.listedSearchText;
    }
    // whatever was picked before, the user is typing again, so the last word is unfinished again
    this.pickedCompletion = undefined;
    this.pickedLine = undefined;
    this.pickedStapleId = undefined;
    let text = this.searchText || '';
    const trimmed = text.trim();

    // typing words only suggests: the full search waits for a commit (#5410). An empty box and a
    // barcode are not words, so they still search after a short pause
    if (trimmed !== '' && !looksLikeBarcode(trimmed)) {
      // a search the previous keystroke scheduled (for an empty box or a barcode) was cancelled
      // above, so its spinner must go too -- unless a committed search is still running
      if (!this.searchOps.isRunning) {
        this.showSpinner = false;
      }
      this.suggestionRows.typingResumed();
      this.suggestController.textChanged(text);
      this.cd.markForCheck();
      return;
    }
    this.clearSuggestions();

    /* Start showing spinner immediately when user starts typing, even though we start the search only after the debounce
     This makes the UI look more responsive. */
    this.showSpinner = true;
    this.cd.markForCheck();
    // debounce the input typing to prevent too many requests to the databases
    this.searchDebouncerTimeout = setTimeout(() => {
      void this.searchFoodsAsync(text);
    }, 300);
  }

  /** Put a new list of suggestion rows under the search box. */
  private showSuggestionList(list: SuggestionList) {
    this.suggestionRows.showList(list);
    this.safeMarkForCheck(this.cd);
  }

  /** The element id of a suggestion row, so the search box can say which row is highlighted. */
  protected suggestionRowId(position: number): string {
    return `food-suggestion-${position}`;
  }

  /**
   * The staple rows for typed text (food staples plan 4b, #5624).
   *
   * THIS HAS TO ANSWER SYNCHRONOUSLY — it runs on every keystroke and the suggestion list is
   * built from what it returns — so it reads the snapshot the page already warmed
   * (`warmStapleSnapshotAsync`, called when the page opens). Before that finishes there are no
   * rows, which is what the app did before the staples reached the device at all.
   *
   * Since food staples plan 4c (#5625) the rows come from the device's own copy of the staples
   * rather than a list compiled into the app, so a corrected staple reaches people without an app
   * release. The matcher is rebuilt whenever the copy it was built from changes — comparing the
   * array by identity is enough, because the store hands back the same array until it re-reads.
   *
   * WHEN A CORRECTION ACTUALLY ARRIVES: at the page's next warm-up, which happens when the page
   * is opened and again at each search. The store itself only re-reads once its age bound has
   * expired, and it re-reads when asked rather than on a timer — so a person sitting on this page
   * sees a corrected staple at their next search, not the moment the pull lands.
   */
  private stapleRowsFor(text: string): SuggestionRow[] {
    if (!this.warmedStapleRows) {
      return [];
    }
    if (this.stapleSuggesterRows !== this.warmedStapleRows) {
      // BUILD FIRST, then record. The other order leaves a throw stuck: the rows would already be
      // recorded, so the next keystroke would take the identity test as satisfied and dereference
      // a matcher that was never built — a failure on every character typed, not just the first.
      this.stapleSuggester = new StapleSuggester(this.warmedStapleRows, this.wordCompleter.analyzerDeps);
      this.stapleSuggesterRows = this.warmedStapleRows;
    }
    return this.stapleSuggester.rowsFor(text);
  }

  /**
   * Read the device's staple copy into memory, so `stapleRowsFor` and the offline search have it
   * ready when they are asked (food staples plan 4c, #5625).
   *
   * Never awaited by its callers: the staples improve the rows, they are not something the page
   * needs before it can be used, and the store answers "not ready" rather than failing when there
   * is nothing to read yet. It reports the failures worth reporting itself, so nothing is reported
   * again here — but it IS caught, because an uncaught floating promise becomes an unhandled
   * rejection, and this one is started without being awaited.
   *
   * Cheap to call often: the store answers from memory until its own age bound expires.
   */
  private async warmStapleSnapshotAsync(): Promise<void> {
    try {
      const rows = await this.stapleDb.getStapleNamesAsync();
      // an empty answer means "not ready"; keeping the previous rows beats emptying the list
      if (rows.length === 0 || rows === this.warmedStapleRows) {
        return;
      }
      this.warmedStapleRows = rows;
      // the search headers come from the same snapshot; reading them now means the offline search
      // never has to wait for a read of its own
      this.warmedStapleHeaders = await this.stapleDb.getAllStapleHeadersAsync();
      this.preparedStapleWords = await this.stapleDb.getPreparedWordsAsync();
      // A NEW SNAPSHOT MEANS NEW PICTURES, open list or not (staple pictures plan, #5694). The cards
      // in the default "logged before" list are the case this exists for: they render from the user's
      // own frozen copies, which carry no picture name, so without a redraw here they keep showing no
      // picture on a cold first open even though the snapshot has just arrived.
      this.safeMarkForCheck(this.cd);
      // ASK FOR THE SUGGESTIONS AGAIN. The rows were not there when the list on screen was built —
      // that is the whole reason this read exists — and nothing else would rebuild it until the next
      // keystroke. Without this, somebody who typed before the staples were ready sits looking at a
      // list with no staple rows in it, with no way to know a second keystroke would bring them.
      //
      // ONLY while the list is OPEN. A committed search deliberately closes it and blurs the box, and
      // re-asking then would pop it open again over the results with the keyboard already shut.
      //
      // `listOpen` rather than `rowsShown`, and the difference is the case this whole re-ask exists
      // for: a list that is open and EMPTY because nothing matched. That is the ordinary shape
      // offline, where the server contributes nothing and the staples are the only rows there will
      // be — so testing "are rows on screen" would skip exactly the person this is meant to help.
      if (this.searchText !== '' && this.suggestionRows.listOpen) {
        this.suggestController.textChanged(this.searchText);
        this.safeMarkForCheck(this.cd);
      }
    } catch (error) {
      // The store contains every failure it knows about and reports the ones worth reporting, so
      // reaching here means something it did NOT anticipate — which is exactly why it gets its own
      // name rather than being folded into the store's. Caught at all because this promise is
      // started without being awaited, and an uncaught one becomes an unhandled rejection.
      this.logging.trackSilentProblem('staple warm-up failed', { error: describeError(error) });
    }
  }

  /** The name a screen reader gives one row: the line, and how many foods hold it when that is known. */
  protected suggestionRowLabel(row: SuggestionRow): string {
    if (row.stapleId !== undefined) {
      return LL.foods.suggestions.stapleRowLabel({ name: row.text });
    }
    return row.count === undefined ? row.text : LL.foods.suggestions.rowLabel({ phrase: row.text, count: row.count });
  }

  /**
   * Take the suggestion rows off the screen and stop anything pending.
   *
   * Needed wherever the search box is filled in for the user rather than typed into -- a barcode
   * scan, or a food name carried in from another screen -- and whenever a search is committed.
   */
  private clearSuggestions() {
    this.suggestController.cancel();
    this.pickedCompletion = undefined;
    this.suggestionRows.clear();
    this.safeMarkForCheck(this.cd);
  }

  /**
   * Keys in the search box (#5410). The rows decide what the key means; the
   * page runs a pick or a commit, because committing a search is the page's own job.
   */
  protected handleSearchKeydown(ev: KeyboardEvent) {
    const action = this.suggestionRows.keyPressed(ev.key, this.searchText);
    if (action.preventDefault) {
      ev.preventDefault();
    }
    switch (action.kind) {
      case SuggestionKeyActionKind.PickRow:
        this.pickSuggestion(action.row, action.position);
        break;
      case SuggestionKeyActionKind.CommitTypedText:
        this.commitTypedText();
        break;
      case SuggestionKeyActionKind.None:
        // the highlight moved or the rows closed
        if (action.preventDefault) {
          this.cd.markForCheck();
        }
        break;
    }
  }

  /** The search box lost focus: rows still on screen with none picked are abandoned (#5410). */
  protected handleSearchBlur() {
    this.suggestionRows.searchBoxBlurred(this.searchText);
    this.cd.markForCheck();
  }

  /**
   * The user picked a suggestion row, by tapping it or with Enter on the highlighted one (#5410).
   * The whole line goes into the box and the full search runs once, now.
   */
  protected pickSuggestion(row: SuggestionRow, position: number) {
    this.suggestionRows.reportPicked(row, position, this.searchText);
    // a staple row shows the full name but searches its short name (food staples plan 4b)
    this.searchText = row.stapleSearchText ?? row.text;
    this.commitSearch();
    // set AFTER the commit clears it, so the picked line's last word counts as settled
    this.pickedCompletion = this.wordCompleter.lastWordOf(this.searchText);
    this.pickedLine = this.searchText.trim();
    this.pickedStapleId = row.stapleId;
    void this.searchFoodsAsync(this.searchText);
    // a staple row is a real food's name, so there is no spelling to correct (food staples plan 4b)
    if (row.stapleId !== undefined) {
      return;
    }
    // a picked line can itself be a misspelling: picking `avacado` after typing `avaca` is one of
    // the two ways the owner found of landing on a misspelled search (#5410)
    void this.considerCorrectionAsync(this.searchText);
  }

  /** Enter (or the phone's Search key) with no row highlighted: search exactly what was typed. */
  private commitTypedText() {
    this.commitSearch();
    void this.searchFoodsAsync(this.searchText);
    void this.considerCorrectionAsync(this.searchText);
  }

  /**
   * Work out what to say about the spelling of a search that has just been committed, and say it
   * (#5410). The search for what was typed is already running: a correction either appears above
   * those results as an offer, or replaces them with the corrected search.
   *
   * Deliberately not awaited by the commit. A correction is an improvement on results the person
   * is already getting, so making them wait for it would cost every correctly-spelled search a
   * round trip to learn that nothing was wrong.
   */
  private async considerCorrectionAsync(committedText: string) {
    const text = committedText.trim();
    if (text === '' || text === this.searchChosenAtTheCard) {
      return;
    }
    const generation = ++this.correctionGeneration;
    const decided = await this.suggestController.correctionForAsync(text);
    // a newer commit happened while this was in flight, or the page went away
    if (generation !== this.correctionGeneration || this.viewDestroyed) {
      return;
    }
    if (decided.kind === SearchCorrectionKind.None) {
      return;
    }
    this.correction = decided;
    this.correctionTypedText = text;
    this.telemetry.trackEvent('foods_search_correction_shown', {
      correctionKind: decided.kind,
      searchText: text,
      correction: decided.phrase,
    });
    if (decided.kind === SearchCorrectionKind.Apply) {
      this.searchText = decided.phrase;
      void this.searchFoodsAsync(decided.phrase);
    }
    this.safeMarkForCheck(this.cd);
  }

  /** The offered correction was taken: search it instead of what was typed. */
  protected takeOfferedCorrection() {
    const phrase = this.correction.phrase;
    this.telemetry.trackEvent('foods_search_correction_taken', { correctionKind: this.correction.kind, correction: phrase });
    // the correction becomes the search, and nothing more is said about it
    this.searchChosenAtTheCard = phrase;
    this.correction = NO_SEARCH_CORRECTION;
    this.searchText = phrase;
    this.commitSearch();
    void this.searchFoodsAsync(phrase);
    this.safeMarkForCheck(this.cd);
  }

  /** "Search <what I typed> instead": run the original search and stop correcting it. */
  protected refuseCorrection() {
    const typed = this.correctionTypedText;
    this.telemetry.trackEvent('foods_search_correction_refused', { correctionKind: this.correction.kind, searchText: typed });
    this.searchChosenAtTheCard = typed;
    this.correction = NO_SEARCH_CORRECTION;
    this.searchText = typed;
    this.commitSearch();
    void this.searchFoodsAsync(typed);
    this.safeMarkForCheck(this.cd);
  }

  /** Stop saying anything about the spelling of the search on screen. */
  private clearCorrection() {
    this.correction = NO_SEARCH_CORRECTION;
    this.correctionTypedText = '';
    // a newer generation makes any lookup still in flight land on nothing
    this.correctionGeneration++;
  }

  /**
   * The results with the picked staple on top (food staples plan 4b). The staple is usually among
   * them already and is moved up; if the server did not return it, it is taken from the device's
   * own copy, and only failing that read by its id from the server. When it cannot be had at all,
   * or the filters on screen exclude it, the results are left as they are.
   */
  private async withStapleFirstAsync(rankedFoods: FoodForUI[], stapleId: string): Promise<FoodForUI[]> {
    const alreadyThere = moveStapleToFront(rankedFoods, stapleId);
    if (alreadyThere) {
      return alreadyThere;
    }

    // THE DEVICE'S OWN COPY FIRST (food staples plan 4c, #5625). It needs no network, so it also
    // works offline, where the server read below cannot succeed at all — which is the whole point
    // of keeping the staples on the device.
    const onDevice = await this.stapleDb.getStapleByIdAsync(stapleId);
    if (onDevice) {
      // Read it back through `FoodDbService` rather than using the snapshot document directly: its
      // staple branch answers from the very same snapshot with no network, and it is where the
      // person's own `local` block — the like mark and the last-used date — is merged onto the
      // curated values (Requirement C5). Without that, this row would be the one place a staple
      // appeared stripped of the history that puts a food somebody eats near the top.
      const merged = await this.foodDb.getFoodByIdAsync(stapleId,
        { cacheFood: false, onReadFailure: FoodReadFailurePolicy.TreatAsMissing });
      if (merged) {
        return this.withStapleRowFirst(rankedFoods, stapleDeviceRow(merged));
      }
    }

    // offline, a read by id cannot succeed and nothing is wrong
    if (!this.onlineState.isLikelyOnline()) {
      return rankedFoods;
    }
    const staple = await this.readPickedStapleAsync(stapleId);
    if (!staple) {
      return rankedFoods;
    }
    const waitForMeasures = this.settings.foodValuesDisplayMode !== FoodValuesDisplayMode.Per100g;
    return this.withStapleRowFirst(rankedFoods, serverStapleRow(staple, waitForMeasures, this.stapleRowDeps));
  }

  /** how `staple-result-row.ts` formats the numbers a row shows */
  private get stapleRowDeps() {
    return { formatNutrients: (nutrients: Nutrients) => FormatNutrients(nutrients) };
  }

  /** Put a staple row on top of the results, unless the filters on screen exclude it. */
  private withStapleRowFirst(rankedFoods: FoodForUI[], row: StapleResultRow): FoodForUI[] {
    if (applyFoodFilters([row], this.filters).length === 0) {
      return rankedFoods;
    }
    return [row, ...rankedFoods];
  }

  /**
   * Read the picked staple by its id, for `withStapleFirstAsync`. The results wait for this read,
   * so it gets at most `PICKED_STAPLE_READ_LIMIT_MS`: the read first waits for the food database
   * to be attached, which can take long or never happen, and one row is not worth holding the
   * whole list for. Answers undefined when the staple is not there, cannot be read, or is late,
   * each reported once under its own name.
   */
  private async readPickedStapleAsync(stapleId: string): Promise<Food | undefined> {
    const timedOut = Symbol('timed out');
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    const deadline = new Promise<typeof timedOut>(resolve => {
      timer = this.safeTimeout(() => resolve(timedOut), PICKED_STAPLE_READ_LIMIT_MS);
    });
    try {
      const answer = await Promise.race([this.foodDb.readFoodsByIdsFromGlobalDbAsync([stapleId]), deadline]);
      // if the read is late, the list goes out without the staple; the search itself is fine
      if (answer === timedOut) {
        this.logging.trackSilentInfo('picked staple read too slow', { stapleId, limitMs: PICKED_STAPLE_READ_LIMIT_MS });
        return undefined;
      }
      const staple = answer.get(stapleId);
      if (!staple) {
        this.logging.trackSilentProblem('picked staple not found', { stapleId });
      }
      return staple;
    } catch (error) {
      this.logging.trackSilentProblem('picked staple read failed', { stapleId, error: describeError(error) });
      return undefined;
    } finally {
      this.cancelTimeout(timer);
    }
  }

  /** What every commit does before searching: close the rows and the keyboard, drop what is pending. */
  private commitSearch() {
    this.clearCorrection();
    if (this.searchDebouncerTimeout) {
      clearTimeout(this.searchDebouncerTimeout);
    }
    this.clearSuggestions();
    // blurring closes the phone's keyboard; the rows are already gone, so the blur handler has
    // nothing left to report as abandoned
    this.searchInputComponent?.setBlur();
  }

  private searchOps = new SearchOperationTracker<SearchOperation>();
  async searchFoodsAsync(searchText: string) {
    // Keep the device's staple copy warm. Cheap: the store answers from memory until its own age
    // bound expires, and only then does it re-read — which is what lets a corrected staple reach
    // somebody who has been sitting on this page, instead of waiting for them to leave and return.
    void this.warmStapleSnapshotAsync();
    // separate into two scopes to prevent accidentally using untrimmed searchText
    // This can easily happen due to autocomplete and can cause subtle bugs that are hard to find.
    return this.searchFoodsAsync_internal(searchText.trim());
  }

  /**
   * Runs the search on screen again, even when the typed text has not changed.
   *
   * `searchOps.start` refuses a repeat of the text already in flight, which is right for the
   * typing path -- the debouncer and the Enter/suggestion-pick commits can ask for the same text
   * twice and only one search should run. It is wrong for a caller that has changed something the
   * search READS rather than the text it searches for: that run is carrying stale input whatever
   * its text says, so clearing the in-flight operation first is what lets the new one start.
   *
   * Dropping the in-flight operation is safe. Every place that could let it damage the newer search
   * already asks whether it is still the current one: both cancellation checks in
   * `runSearchOperationAsync`, the per-food loop in `loadAllFoodDetailsAsync`, `searchOps.end`
   * and the spinner in `searchFoodsAsync_internal`'s `finally`.
   *
   * Callers: a retry after a connection failure (#3743); a filter the user picked; a food saved or
   * deleted (`FoodUpdatedEvent`); and the "Show food values by" choice. Each of the last three used
   * to be dropped whenever a search for the same text was still running (df-1f667923b17d).
   */
  private restartSearch() {
    this.searchOps.supersede();
    void this.searchFoodsAsync(this.searchText);
  }

  /** re-run the current search after a connection failure (#3743) */
  protected retrySearch() {
    this.restartSearch();
  }

  async searchFoodsAsync_internal(trimmedText: string) {
    // A NEW search is a new set of results to accept or dismiss, so whatever the user did with
    // the previous one stops counting. A REFRESH of the same search is not — and this method
    // serves both. It is re-run when a food is edited and saved (the `FoodUpdatedEvent`
    // subscription in `ngOnInit`), when the page is re-entered with no results cached, after a
    // connection failure (`retrySearch`), and on every filter and tab change.
    //
    // Telling them apart by the TEXT rather than by the caller is deliberate: there are about
    // twenty call sites, and one of them tagged wrongly is a silent wrong number. It also errs
    // in the safe direction — this can only ever let the flag stay true for longer, never make
    // it true when the user picked nothing, and it is a FALSE "rejected" that is the bug.
    //
    // Without this, a food edited mid-log wiped the flag: search, tap a food, View details,
    // edit and save it, come back, press Log, leave — the refresh in the middle reset the flag
    // and Log never set it again, so a search the user logged from reported as dismissed.
    if (trimmedText !== this.searchTextBehindSelectionAccepted) {
      this.searchTextBehindSelectionAccepted = trimmedText;
      this.selectionAccepted = false;
    }
    // we assume it is a barcode search if the text has at least 8 digits and no letters -- the
    // same rule the suggestions use to keep a barcode out of them (#5410)
    this.searchIsBarcode = looksLikeBarcode(trimmedText);
    // if it is barcode search, cleanup the text to match the barcode format
    if (this.searchIsBarcode) {
      trimmedText = trimmedText.replace(/[^0-9]/g, '');
    }

    let op = this.searchOps.start(trimmedText, { isBarcodeSearch: this.searchIsBarcode, allHeaders: [], loadingFoods: [] });
    if (op === undefined) {
      return;
    }
    this.listedSearchText = trimmedText;
    this.dismissalReportedFor = undefined;

    // clear any error state from a previous search before this one runs
    this.serverSearch = ServerSearchOutcome.Contributed;
    this.searchFailed = false;
    this.showSpinner = true;
    this.cd.markForCheck();

    try {
      await this.runSearchOperationAsync(op);
    } catch (err) {
      // #4100: a search that fails has to SAY so. Before this, nothing caught the error, so
      // the spinner stayed on forever and the page went on believing this search was still
      // running — which made a retry of the same text a no-op, and left the user with a
      // spinner and no way out. Reported as a problem because the user is now looking at a
      // failure state and can do nothing about it.
      //
      // But only replace the list when this search never got one on screen. The pass keeps
      // working after it publishes — it fills in each food's details — so a failure in that
      // later part would otherwise wipe a perfectly good list the user is already reading and
      // put an error card in its place, which is worse than the bug being fixed here.
      if (this.isStillCurrentSearch(op) && !op.published) {
        this.searchFailed = true;
        this.foods = [];
      }
      // one report per failure. Two kinds of error arriving here have already reported
      // themselves, each with more detail than this site has: a read that could not produce
      // the default food list (which read failed, and how long it waited), and a real server
      // search failure (the error described by describeErrorForLog, which keeps the stack, the
      // hidden fields and the cause chain, plus the query as a breadcrumb).
      // Reporting either again would file the same failure twice, under two names nothing
      // pairs up. Everything else still reports here — the point is one report, not fewer.
      if (!(err instanceof DefaultFoodsUnavailableError) && !(err instanceof ReportedSearchFailureError)) {
        const context = {
          isBarcodeSearch: op.isBarcodeSearch,
          hasSearchText: op.text.length > 0,
          publishedBeforeFailing: !!op.published,
          error: describeError(err),
        };
        // a read cut short by our own sign-out, a database recovery or a passing network failure
        // is not a fault in the search. the card above still shows, and the next search usually
        // works, so it is info under its own name (#6350). isPassingSearchReadFailure says which
        // refusals count, and why a missing database handle is not one of them.
        if (isPassingSearchReadFailure(err)) {
          this.logging.trackSilentInfo('FoodSearchReadUnavailable', context);
        } else {
          // the stack as well, so the report names the function that threw. the message alone
          // could not tell which of several id lookups failed in #6350
          this.logging.trackSilentProblem('FoodSearchFailed', { ...context, errorDetail: describeErrorForLog(err) });
        }
      }
    } finally {
      // the spinner ALWAYS stops and the operation ALWAYS ends, whatever happened above.
      // Guarded on this still being the current search, so a slow older search that finishes
      // late can never clear a newer search's spinner.
      if (this.isStillCurrentSearch(op)) {
        this.showSpinner = false;
      }
      this.searchOps.end(op);
      this.cd.markForCheck();
    }
  }

  /** True while `op` is the search the page is currently showing. Same test as
   *  searchOps.shouldCancel, without its "cancelling" profiling log — this is asked on
   *  paths where the search was NOT cancelled, and logging one there would mislead. */
  private isStillCurrentSearch(op: SearchOperation) {
    return this.searchOps.isCurrent(op);
  }

  /** Records how `op`'s server-side search ended, but only while `op` is still the search on
   *  screen: a superseded older search finishing late must never flip a newer, successful one
   *  into a failure state (#3743). Every place that sets `serverSearch` on a failure goes
   *  through here, so that guard cannot be forgotten at one of them. */
  private recordServerSearchOutcome(op: SearchOperation, outcome: ServerSearchOutcome) {
    if (this.isStillCurrentSearch(op)) {
      this.serverSearch = outcome;
    }
  }

  /** The search itself, top to bottom: read the headers, filter them, rank them, publish the
   *  result. Extracted so searchFoodsAsync_internal's try/catch/finally reads as one piece and
   *  can cover the whole pass (#4100). */
  private async runSearchOperationAsync(op: SearchOperation) {
    if (this.searchOps.shouldCancel(op)) {
      return;
    }

    // A barcode names one exact product, so a type or a source filter that would hide it is set
    // aside for as long as that barcode's results are on screen — including a barcode the user
    // typed in by hand. The next search that is not a barcode puts them back, without touching
    // what the user chose or what a route asked for (#5549).
    //
    // Set once per barcode, not on every search of it: a filter the user picks WHILE looking at
    // barcode results re-runs the same search, and rewriting the layer here would undo their tap
    // the moment they made it.
    if (!op.isBarcodeSearch) {
      this.scanFilters = {};
      this.scanFilterBarcode = undefined;
    } else if (this.scanFilterBarcode !== op.text) {
      this.scanFilterBarcode = op.text;
      this.scanFilters = { type: FoodTypeFilter.All, hideUnverified: false };
    }

    let allHeadersUnfiltered: FoodHeader[] = [];

    if (op.isBarcodeSearch) {
      // searchFoodHeadersByEANAsync isolates a transient elastic failure on its
      // own, so the user's local barcode matches still survive a connection blip
      // (#3743). It reports the connection error via a flag rather than throwing,
      // so local results are never discarded. Real (non-transient) errors still
      // propagate out of it as before.
      let eanResult = await this.foodDb.searchFoodHeadersByEANAsync(op.text);
      allHeadersUnfiltered.push(...eanResult.headers);
      // only honor the connection error for the search that is still current — a
      // superseded older search's late failure must not flip a newer, successful
      // search into the connection-error state (#3743).
      if (eanResult.connectionError) {
        this.recordServerSearchOutcome(op, ServerSearchOutcome.Unreachable);
      }
    } else {
      // elastic search now propagates real errors (non-2xx, non-transient)
      // instead of returning silent [] (#3246). isolate it from the local
      // searches so a search-only failure doesn't blank out the user's saved
      // foods, and surface the failure via trackSilentProblem.
      let [localFoods, localMeals, localRecipes] = await Promise.all([
        this.foodDb.searchFoodByName_userAsync(op.text),
        this.foodDb.searchRecentMealsByName_userAsync(op.text),
        // a line picked from the suggestions is searched as it stands, without typo tolerance (#5579)
        this.foodDb.searchFoodHeadersByName_elasticAsync(op.text, this.maxListLen,
          op.text === this.pickedLine ? TypoTolerance.None : TypoTolerance.OneLetter)
          .catch(err => {
            // however it failed, the server search did NOT contribute, so the list is about to
            // be nothing but this device's cached foods — which is what the card above the list
            // explains (#5114). A transient failure is still told apart from a real one: only
            // the real one is reported to Sentry, so passing blips stay out of it (#3743).
            if (err instanceof SearchConnectionError) {
              this.recordServerSearchOutcome(op, ServerSearchOutcome.Unreachable);
            } else {
              this.recordServerSearchOutcome(op, ServerSearchOutcome.Failed);
              // report only a failure that has NOT already been reported. A real server
              // failure is reported by the search client, which throws
              // ReportedSearchFailureError to say so — reporting it again here filed a second
              // Sentry event for one cause, under a different name that nothing pairs up, so
              // every count of failed searches came out roughly double. Anything else
              // reaching here (a non-timeout rejection of the sign-in wait, say) was never
              // reported by anybody, so it still gets its one report.
              if (!(err instanceof ReportedSearchFailureError)) {
                this.foodDb.logging.trackSilentProblem('Elastic recipe search failed', {
                  query: op.text,
                  error: describeError(err),
                });
              }
            }
            return [] as FoodHeader[];
          }),
      ]);

      allHeadersUnfiltered.push(...localFoods, ...localMeals, ...localRecipes);
    }

    // When the server could not answer, add the staples this device holds (food staples plan 4c,
    // #5625). BEFORE the filters on the next line, on purpose: a staple is an ordinary food as far
    // as the type filter, Hide Unverified and the list cap are concerned, and one that slipped past
    // them would be a food the person had asked not to see. The rules live in
    // staple-offline-search.ts.
    allHeadersUnfiltered = withOfflineStaples(
      allHeadersUnfiltered,
      this.warmedStapleHeaders ?? [],
      this.serverSearch === ServerSearchOutcome.Contributed,
      op.isBarcodeSearch,
      { buildRow: staple => stapleDeviceRow(staple) });

    // a row with no usable _id would make the id lookups in the ranking below throw, which
    // replaced the whole list with the error card (#6350). every source has joined the list by
    // now (own foods, recent meals, server rows, staples), so this one check covers them all.
    // the row is left out and reported, so whatever produced it can be found.
    const { kept: headersWithId, dropped: headersWithoutId } = splitRowsByUsableId(allHeadersUnfiltered);
    if (headersWithoutId.length > 0) {
      this.logging.trackSilentProblem('FoodSearchRowWithoutId', {
        read: op.isBarcodeSearch ? 'barcodeSearchRows' : 'searchRows',
        count: headersWithoutId.length,
        sample: describeRowWithoutId(headersWithoutId[0]),
      });
    }
    allHeadersUnfiltered = headersWithId;

    // keep only the foods that pass the filters; the rules live in foods-filters.ts
    op.allHeaders = applyFoodFilters(allHeadersUnfiltered, this.filters);

    // ----------------- from here on this method is the search lifecycle -----------------

    // a search that was superseded while its reads were running must not publish anything.
    // Before #4100 this check did not exist, and it did not matter because a stuck search
    // never finished at all. Now that every search finishes, a slow one that lands late
    // would otherwise overwrite the results the user is currently looking at.
    if (this.searchOps.shouldCancel(op)) {
      return;
    }

    // when all headers are found, proceed to actual matching
    let rankedFoods = this.rankHeadersForDisplay(op);
    // if this search is for a picked staple row, that staple goes first (food staples plan 4b)
    if (this.pickedStapleId !== undefined && op.text === this.pickedLine) {
      rankedFoods = await this.withStapleFirstAsync(rankedFoods, this.pickedStapleId);
    }

    // second cancellation check, right before publishing: the ranking above and the reads that
    // fed it can both take a while, so the search may have been superseded since the check
    // just above (#4100).
    if (this.searchOps.shouldCancel(op)) {
      return;
    }

    // the per-food detail load reads its list off the operation, so the published list has to
    // be there as well as on screen. Both names point at the SAME array, which is what lets
    // that load fill each row in behind a list the user is already reading.
    op.loadingFoods = rankedFoods;
    this.foods = rankedFoods;
    // remember that this search reached the screen. Everything below this line is filling in
    // per-food detail, and if THAT fails the caller must not replace the list the user is
    // already reading with an error card (#4100).
    op.published = true;
    // stop the spinner here rather than waiting for searchFoodsAsync_internal's finally, so the
    // list appears as soon as it is ready and the per-food details fill in behind it. That
    // finally clears it again, which is what covers the paths that never reach this line.
    this.showSpinner = false;
    this.cd.markForCheck();
    // AWAITED, not started and forgotten: that is what puts it inside
    // searchFoodsAsync_internal's try/catch/finally, and so what guarantees the spinner stops
    // even when this step is the one that fails (#4100).
    await this.loadAllFoodDetailsAsync(op);
    this.cd.markForCheck();
  }

  /**
   * Ranks the headers this search found into the list the page will show: scores every
   * candidate on how well it matches the typed text and how recently it was used, drops the
   * ones that match too badly, sorts and merges the cloud and local results, and caps the
   * result at `maxListLen`.
   *
   * Ranking ONLY. It holds no part of the search lifecycle — it does not check whether a newer
   * search has superseded this one, does not assign `this.foods`, does not set `op.published`
   * and does not touch the spinner. All of that lives in `runSearchOperationAsync`, so that a
   * change to either concern no longer has to read the other (df-cac9be904e33).
   *
   * It does still READ the page state it needs in order to rank — the current time, the type
   * filter, two display settings and the list cap.
   */
  private rankHeadersForDisplay(op: SearchOperation): FoodForUI[] {
    let now = this.debug.getNowLocal().toUTC();

    // split text into words the same way the search index does, so a name is judged here by the
    // same rule the server would judge it by (food-search-match.ts explains what is mirrored)
    const analyzerDeps = this.wordCompleter.analyzerDeps;

    // finish off the word the user may still be typing, against the dictionary of real food
    // words, so the matcher below only ever compares WHOLE words -- the one rule the client and
    // the Elasticsearch server share (#5319). A word the user picked from a suggestion counts as
    // finished (that rule lives in the completer).
    const completedQuery = this.wordCompleter.searchTextQuery(op.text, this.pickedCompletion);
    // order foods by source tier and then by fewer words besides the search (#5550, #4970).
    // Never for the empty box, which is history only
    const rankBySourceTiers = op.text.length > 0 || op.isBarcodeSearch;

    let maxLen = this.maxListLen;
    let rankedFoods: FoodForUI[] = [];

    // score, filter and order the foods; the same ranking the food scanners use (#5590)
    const sortedHeaders: FoodHeader[] = rankFoodHeaders(op.allHeaders, {
      now,
      text: op.text,
      completedQuery,
      analyzerDeps,
      rankBySourceTiers,
      isBarcodeSearch: op.isBarcodeSearch,
      boostRecentLogMeals: this.filters.type === FoodTypeFilter.All,
      debugDescriptions: !!this.settings.debugFoodSearch,
      // the staples' names and aliases already split and stemmed, so an offline search does not
      // redo that for every one of them on every keystroke (food staples plan 4c, #5625). A food
      // with no entry here is ranked exactly as before.
      preparedWords: this.preparedStapleWords,
    });

    for (let i = 0; i < sortedHeaders.length; i++) {
      let h = sortedHeaders[i];
      let newItem = h as FoodForUI;
      this.insertFoodToUI(rankedFoods, i, newItem, maxLen);
    }

    // if showing per-serving values, pre-mark header-only foods as loading BEFORE first render
    // this prevents showing per-100g nutrients briefly before the skeleton appears
    if (this.settings.foodValuesDisplayMode !== FoodValuesDisplayMode.Per100g) {
      for (const food of rankedFoods) {
        if (food && !food.cachedFood && !isLogMealById(food._id)) {
          food.measuresLoading = true;
        }
      }
    }

    return rankedFoods;
  }

  /** where this device keeps the filters the user last chose; cleared on sign-out with every other
   *  unregistered key (`local-storage-hygiene.ts`), so nobody inherits the last user's filters */
  private static readonly FILTERS_STORAGE_KEY = 'cs_foods_filters';

  /**
   * Reads the filters this device remembers for the signed-in user. Anything unusable — nothing
   * saved, another account's filters, a save older than an hour, a damaged value — leaves the
   * default filters in place, and only a damaged value is reported (it is data to repair, not a
   * failure to hide).
   */
  private readRememberedFilters() {
    let raw: string | null = null;
    try {
      raw = localStorage.getItem(FoodsPage.FILTERS_STORAGE_KEY);
    } catch (err) {
      // the storage getter itself throws when site data is blocked; the page still works without
      // remembered filters, so this is reported and the defaults stand
      this.logging.trackSilentInfo('FoodsFiltersStorageUnreadable', { error: describeError(err) });
      return;
    }
    const restored = restoreRemembered(raw, this.auth.user?.uid ?? '', this.debug.getNowLocal());
    this.rememberedFilters = restored.state;
    if (restored.repaired) {
      this.logging.trackSilentInfo('FoodsFiltersRestoreRepaired', { reason: restored.reason });
    }
  }

  /** writes the user's own filters and the time, which is what the hour above is counted from */
  private saveRememberedFilters() {
    const uid = this.auth.user?.uid;
    // a signed-out session has nobody to remember filters for
    if (!uid) {
      return;
    }
    try {
      localStorage.setItem(FoodsPage.FILTERS_STORAGE_KEY,
        serializeRemembered(this.rememberedFilters, uid, this.debug.getNowLocal()));
    } catch (err) {
      // storage can be full or blocked; the filters then last only as long as this page object
      this.logging.trackSilentInfo('FoodsFiltersStorageUnwritable', { error: describeError(err) });
    }
  }

  /**
   * Applies the filter a route asked for, for this visit only. Every other field is reset to its
   * default for the visit as well: recent meals carry no liked or logged-before mark and are not
   * the user's own foods, so a remembered My Foods would empty "View All Recent Meals" completely.
   */
  private applyRouteFilter(filter: string | undefined) {
    const forVisit: FoodsFilterState = { ...DEFAULT_FOODS_FILTER_STATE };
    switch (filter) {
      case 'recipes': forVisit.type = FoodTypeFilter.Recipes; break;
      case 'meals': forVisit.type = FoodTypeFilter.Meals; break;
      case 'foods': forVisit.type = FoodTypeFilter.Foods; break;
      // there is no Liked type any more: this route asks for the user's favorites
      case 'liked': forVisit.favorites = true; break;
      default: return;
    }
    this.routeFilters = forVisit;
  }

  /**
   * Records what the user chose and runs the search again. The choice goes into the remembered
   * layer and is removed from the route and scan layers, so turning off a filter a route set is
   * remembered like any other tap rather than being immediately overridden again.
   *
   * The re-run goes through `restartSearch`, not `searchFoodsAsync`. A search already in flight for
   * the same typed text read the filters once, near the start of `runSearchOperationAsync`, so it
   * is carrying the OLD ones and cannot pick the new choice up. Asking for a plain search here was
   * refused as a repeat of that run and nothing retried it, which left the chip switched on beside
   * a list that still ignored it until the user searched again.
   */
  private changeFilters(change: Partial<FoodsFilterState>) {
    this.rememberedFilters = { ...this.rememberedFilters, ...change };
    for (const field of Object.keys(change) as (keyof FoodsFilterState)[]) {
      delete this.routeFilters[field];
      delete this.scanFilters[field];
    }
    this.saveRememberedFilters();
    this.restartSearch();
  }

  /** picks a type, from the Type picker, the type chip's × or the Filters menu */
  protected setTypeFilter(type: FoodTypeFilter) {
    if (this.filters.type !== type) {
      this.telemetry.trackEvent('foods_filter_by_type', { value: type === FoodTypeFilter.All ? 'any' : type });
    }
    this.changeFilters({ type });
  }

  /** turns one on-or-off filter on or off, from a chip or from the Filters menu */
  protected setFilter(name: FoodFilterName, on: boolean, via: 'chip' | 'menu') {
    this.telemetry.trackEvent('foods_filter_toggle', { filter: FILTER_TELEMETRY_NAMES[name], on, via });
    this.changeFilters({ [name]: on });
  }

  /** a tap on a chip: an off chip turns its filter on, and the × on an on chip turns it off */
  protected handleChipClick(chip: FoodFilterChip) {
    this.setFilter(chip.id, !chip.on, 'chip');
  }

  /** the type chip: tapping the name opens the picker, wherever the chip currently stands */
  protected handleTypeChipClick(event: Event) {
    void this.popupCtrl.createMenu({
      title: LL.foods.filters.type(),
      value: this.filters.type,
      buttons: this.typeFilterList.map(type => ({
        text: this.typeFilterLabel(type),
        value: type,
        handler: () => this.setTypeFilter(type),
      })),
      targetFromElement: event.currentTarget as HTMLElement,
    });
  }



  private insertFoodToUI(items: FoodForUI[], newIndex: number, newItem: FoodForUI, maxLen: number) {
    newItem.displayNutrients = newItem.displayNutrients || [];
    for (let nutrient of newItem.displayNutrients) {
      delete nutrient.target;
    }

    let foodSourceInfo = foodSourceInfoTable[getFoodSource(newItem._id)];
    newItem.sourceShortName = foodSourceInfo.shortName;

    items.splice(newIndex, 0, newItem);
    if (items.length > maxLen) {
      items.splice(maxLen, items.length - maxLen);
      console.assert(items.length === maxLen);
    }
  }

  private async loadAllFoodDetailsAsync(op: SearchOperation) {
    // first get all cached foods that might be available
    let cachedFoods = await this.userDb.getCachedFoodsMapByIdsAsync(op.loadingFoods.map(f => f._id));

    let throttler = 0;
    for (let uiFood of op.loadingFoods) {
      if (!uiFood) {
        continue;
      }
      if (this.searchOps.shouldCancel(op)) {
        return;
      }
      if (!uiFood.cachedFood) {
        uiFood.cachedFood = cachedFoods[uiFood._id];
      } else {
        /* Must throttle down the speed at which we are adding nutrients to UI, because
        they are expensive to create and it's easy to hang the UI refresh loop.
        */
        if (throttler++ % 10 === 0) {
          await sleep(0);
        }
      }
      if (uiFood.cachedFood) {
        if (isLogMeal(uiFood.cachedFood)) {
          // a log meal stores no per-100g values, so its card reads this pre-formatted list.
          // it is the only kind that still does: everything else is scaled on demand by
          // getFoodDisplayNutrients, which is the single owner of the card's numbers.
          uiFood.displayNutrients = FormatNutrients(uiFood.cachedFood.nutrients, this.userDb);

          // generate description for log meals
          const [description] = await this.foodDb.generateMealTextAsync(uiFood.cachedFood as Log_Meal, this.docWatcher);
          this.foodDescriptions.set(uiFood._id, untranslatedString(description));
        } else {
          // store per-100g nutrients for dynamic display based on user settings
          applyLoadedFoodToRow(uiFood, uiFood.cachedFood);
        }
      }
    }

    // ask the server whether the foods we are showing from the user's OWN copies have changed.
    // background, like the fetch below: the rows are already on screen with what we had, and a
    // refreshed one redraws when the answer comes back
    void this.refreshCachedRowsAsync(op);

    // fetch the full documents for header-only foods in the background. this used to run
    // only when showing per-serving values, because measures were the only thing it was
    // needed for. it now runs always: the "Check Values" badge is worked out from a food's
    // nutrients, and a search hit the user has never logged carries none until this fetch
    // completes -- so skipping it left exactly the foods a fresh search or a barcode scan
    // turns up with no badge, which is the path the badge exists for (#4541).
    void this.loadFullDocsForHeaderOnlyFoodsAsync(op);
  }

  /**
   * Replace the rows that came from the user's own copy of a food, where the server has something
   * different, and redraw them.
   *
   * This is the other half of the freshness check the owner asked for (2026-09-20): a food is
   * asked about the first time it is OPENED, and the first time it is SEEN as a card here. Without
   * this half the list stays as it was until every food has been opened one by one — which for the
   * official recipes means all 190 of them, since every paying user gets a copy of the whole set
   * on first sign in and the list renders straight off those copies.
   *
   * Each food is asked about once per app start, whichever half asks first; `FoodDbService` owns
   * that record. So scrolling a long list, or searching the same word twice, costs one read per
   * food and no more.
   */
  private async refreshCachedRowsAsync(op: SearchOperation) {
    const cachedRows = op.loadingFoods.filter(food => food && food.cachedFood && !isLogMealById(food._id));
    if (cachedRows.length === 0) {
      return;
    }

    // the service never throws: whatever goes wrong, it reports it and leaves every copy alone, so
    // the rows keep showing what they already showed. That is a promise the service makes, not an
    // assumption made here -- see refreshCachedFoodsIfStaleAsync
    const refreshed = await this.foodDb.refreshCachedFoodsIfStaleAsync(cachedRows.map(row => row.cachedFood as Food));
    if (refreshed.size === 0) {
      return;
    }

    for (const row of cachedRows) {
      const freshFood = refreshed.get(row._id);
      if (freshFood) {
        applyLoadedFoodToRow(row, freshFood);
      }
    }
    this.cd.markForCheck();
  }

  /**
   * Background-load the full documents for foods that are header-only (from global search).
   * These foods have no `cachedFood`, so their measures and their nutrients both have to be
   * fetched from the global database.
   *
   * Named for measures until #4541, when the "Check Values" badge gave the nutrients it
   * fetches a second consumer.
   */
  private async loadFullDocsForHeaderOnlyFoodsAsync(op: SearchOperation) {
    // collect foods that need measures (header-only, not meals)
    const headerOnlyFoods = op.loadingFoods.filter(f =>
      f && !f.cachedFood && !isLogMealById(f._id)
    );

    if (headerOnlyFoods.length === 0) {
      return;
    }

    // loading state already set in loadAllFoodDetailsAsync

    try {
      // batch fetch from global DB
      const foodIds = headerOnlyFoods.map(f => f._id);
      const foodsMap = await this.foodDb.getFoodsByIdsFromGlobalDbAsync(foodIds);

      // update foods with fetched data
      for (const uiFood of headerOnlyFoods) {
        const fetchedFood = foodsMap.get(uiFood._id);
        if (fetchedFood) {
          // these are never log meals -- the filter above excludes them -- so the card's
          // numbers come from getFoodDisplayNutrients, which scales these on demand
          applyLoadedFoodToRow(uiFood, fetchedFood);
        }
        uiFood.measuresLoading = false;
      }
    } catch (err) {
      // report it: this read failing is invisible otherwise. it costs the user their
      // serving sizes, and since #4541 it also costs them the "Check Values" badge on
      // exactly the foods it exists for -- a fresh search hit or a barcode scan, neither of
      // which the user has cached. a food that looks unremarkable because a fetch failed is
      // worse than one that looks unremarkable because it is fine, and nothing else here
      // says which happened.
      this.logging.trackSilentProblem('HeaderOnlyFoodFetchFailed', {
        foodCount: headerOnlyFoods.length,
        error: describeError(err),
      });
      // the spinner still has to stop, whatever went wrong
      for (const food of headerOnlyFoods) {
        food.measuresLoading = false;
      }
    }

    this.cd.markForCheck();
  }

  getFoodDescription(food: FoodForUI): TranslatedString {
    return this.foodDescriptions.get(food._id) || untranslatedString('');
  }

  /** This makes sure that when the edit food page is closed, we return the result straight to our caller.
   * The nav params are passed to the EditFoodPage.  */
  async invokeEditFood(navParams: any) {

    // when the edit food is completed, pass that food back to the caller
    console.log(`Foods: Invoking edit food for ${JSON.stringify(navParams)}`);
    void this.navCtrl.pushAsync(IEditFoodPage, {
      ...navParams,
      returnCallback: 'onFoodEdited',
      local: {
        returnContext: { mode: this.mode, editMode: navParams.mode },
      },
    });
  }

  /**
   * Callback invoked by NavController when returning from EditFoodPage.
   * This is called via the returnCallback mechanism in nav-controller.ts.
   */
  onFoodEdited(context: { mode: string; editMode: string }, foodId: string) {
    // if page was closed without saving (e.g. header back button), ignore
    if (!foodId) return;
    // if context is missing (e.g. sessionStorage evicted during iOS backgrounding), Sentry 3SQ
    if (!context) return;

    // A food the user just CREATED is treated exactly like a food they tapped (#4968).
    //
    // The user did not come here to make a food. They came to add one to a meal or to a day,
    // could not find it, and made it so they could add it. Creating it is a detour inside that
    // errand, not the end of it — so finishing the detour puts them where finding the food
    // would have: the portion screen, opened from this list, wired to the same caller. Before
    // this they were left looking at a search list holding a food they had just made, with the
    // meal still waiting underneath, and had to find and tap it themselves.
    //
    // `returnSelectedFoodAsync` is deliberately the same call a tap makes, so the two cannot
    // end differently. It already knows all three shapes this list can be opened in — answer a
    // caller, log to a day, or plain pop — so nothing here needs to know which one applies.
    if (context.editMode === 'createRecipe' || context.editMode === 'createFood') {
      // Only when this list was opened AS PART OF adding a food. Opened for browsing (Settings
      // → Foods), creating a food is the whole errand and the user stays here, which is what
      // it has always done.
      if (this.hasAddFoodIntent()) {
        this.selectionAccepted = true;
        // Through the same guarded call a tap makes, so the two cannot drift (#4967). This
        // list is on screen again the moment the food editor pops and stays tappable until
        // the portion screen is up, and the guard is released again if nothing opens.
        void this.pickFoodUnderGuardAsync(foodId);
      }
      return;
    }

    if (context.mode === 'select') {
      void this.returnSelectedFoodAsync(foodId);
    }
  }

  handleSearchAll() {
    void this.searchFoodsAsync(this.searchText);
  }

  async handleFoodClicked(foodHdr: FoodHeader) {
    if (this.listedSearchText !== '') {
      this.telemetry.trackEvent('foods_search_accepted', { searchText: this.listedSearchText, foodId: foodHdr._id });
    }

    if (this.mode === 'select') {
      // Since #4967 this page can still be on screen, and still taking taps, for the whole
      // time the caller spends reading the food and opening the portion screen — on BOTH
      // select routes, which now push that screen on top of this list rather than replacing
      // it. A second tap in that window starts the work twice. Popping used to make that
      // impossible; now it has to be said out loud.
      //
      // Scoped to select mode, and deliberately NOT wider. The browsing modes below open a
      // page with a dropped promise, so there is nothing to await and no way to tell a pick
      // that opened nothing from one that worked — a guard there could only be released by
      // leaving and coming back, which is the very stranding this exists to prevent. They had
      // no guard before this ticket and are no worse off without one.
      if (this.pickInFlight) {
        return;
      }
      this.selectionAccepted = true;
      await this.pickFoodUnderGuardAsync(foodHdr._id);
    } else {
      // no tap guard in the browsing modes — they open a page with a dropped promise, so
      // there is nothing to await and no way to tell a pick that opened nothing from one that
      // worked. `selectionAccepted` is still set, because it is the telemetry question, not
      // the guard.
      this.selectionAccepted = true;
      if (foodHdr.cachedFood && isLogMeal(foodHdr.cachedFood)) {
        void this.navCtrl.pushAsync(IEditMealPage, {
          id: foodHdr._id
        })
      } else {
        void this.invokeEditFood({ id: foodHdr._id || '', ean: '', name: '', mode: this.mode || 'details' });
      }
    }
  }

  /**
   * Answer with a food, holding the double-tap guard for as long as the answer takes (#4967).
   *
   * Shared by the two ways this list produces a food: the user tapping one, and the user
   * CREATING one (#4968). They must behave identically, and holding a guard is the kind of
   * thing that goes wrong when it is written twice — the created-food path was added without
   * the release below, which strands the list on every later tap if a pick opens nothing.
   *
   * A pick that opened nothing must not leave this list deaf to every later tap.
   * `ionViewDidEnter` clears the flag when the user comes BACK, which covers the normal case,
   * but several paths accept the pick and then open nothing at all: the food could not be read
   * (its alert invites a retry this list would otherwise refuse), the food is missing, the user
   * navigated away mid-lookup, or delivering the result threw. Before #4967 the list popped
   * itself on selection, so none of those could strand it.
   *
   * The test is whether anything ended up on top of us. That answer is only trustworthy
   * because the whole chain is awaited: `returnSelectedFoodAsync` awaits the push (or
   * `deliverResultWithoutPoppingAsync`, which awaits the callback), and the two pages that own
   * that callback return the ingredient list's promise instead of dropping it. Drop it
   * anywhere along there and this check runs before the portion screen exists, clearing the
   * guard on every pick and quietly reducing it to nothing.
   */
  private async pickFoodUnderGuardAsync(foodId: string) {
    this.pickInFlight = true;
    await this.returnSelectedFoodAsync(foodId);
    if (this.navCtrl.getActive()?.instance === this) {
      this.pickInFlight = false;
    }
  }

  /**
   * The errand this list was opened with. This list answers a waiting page with a `FoodsPageResult`.
   * Read once and kept: reading reports a lost context, and a created food reads it twice.
   */
  private get addFoodErrand(): AddFoodErrand {
    this.addFoodErrandRead ??= readAddFoodErrand(this.navParams.data, ErrandAnswerShape.FoodsPageResult,
      (name, context) => this.logging.trackSilentProblem(name, context));
    return this.addFoodErrandRead;
  }
  private addFoodErrandRead: AddFoodErrand | undefined;

  /**
   * Was this list opened as part of adding a food somewhere, rather than for browsing?
   * A list opened to look at foods (Settings → Foods) has no errand, and finishing a food there
   * is the whole task.
   */
  private hasAddFoodIntent(): boolean {
    return hasAddFoodIntent(this.addFoodErrand);
  }

  /**
   * Finish the errand with the food the user tapped or created, through the one shared answer
   * the food editor uses too (#5377), so the two cannot end an errand differently.
   *
   * On a day errand this list opens the portion screen ON TOP of itself (#4967), so Log and Back
   * come back to the list. It used to replace itself, which threw away the search the user had
   * picked from; Menno asked for both to return to the list, and for a second food to be a tap
   * away rather than a fresh search. With a waiting caller that asked for `keepOpenAfterSelect`
   * it answers without closing, for the same reason.
   */
  async returnSelectedFoodAsync(foodId: string) {
    try {
      await answerAddFoodErrandAsync(this.navCtrl, this.addFoodErrand, foodId, ErrandAnswerer.FoodList);
    } catch (err) {
      this.logging.trackAbnormalError(err);
    }
  }

  handleCreateFood() {
    // the barcode is the one that was searched and found nothing -- not whatever has been typed
    // into the box since, which with suggestions on is never searched on its own (#5410)
    if (this.searchIsBarcode) {
      void this.invokeEditFood({ id: '', ean: this.listedSearchText, name: '', mode: 'createFood' });
    } else {
      void this.invokeEditFood({ id: '', ean: '', name: this.searchText, mode: 'createFood' });
    }
  }

  async handleCreateRecipe() {
    void this.invokeEditFood({ id: '', ean: '', name: this.searchText, mode: 'createRecipe' });
  }

  handleScanLabel() {
    void this.navCtrl.pushAsync(ILabelScannerPage, {
      // Carried by the scanner into the food editor it replaces itself with, so the food that
      // gets made comes back HERE and is treated exactly like a food picked off this list
      // (#4968). Without it the label scan ended at a saved food and the errand the user was
      // on — adding something to a meal, logging something to a day — stopped there.
      returnCallback: 'onFoodEdited',
      // The barcode that found nothing, when that is why the user is scanning a label at all.
      // Passing it is what lets the new food carry it, so the same product is found by barcode
      // next time instead of failing the same way again.
      ean: this.searchIsBarcode ? this.listedSearchText : undefined,
      local: {
        returnContext: { mode: this.mode, editMode: 'createFood' },
      },
    });
  }

  async onBarcodeScannedAsync(_context: unknown, code: string) {
    // if user cancelled or no code was scanned
    if (!code) {
      return;
    }
    this.telemetry.trackEvent('barcode_scanned', { ean: code });
    // the filters a barcode search sets aside are applied by the search itself, so a barcode the
    // user typed by hand is treated the same way (see runSearchOperationAsync)
    // just put the barcode in the search field
    this.searchText = code;
    // a barcode has no words to finish, and any row left over from what was typed before would
    // now be offering to rewrite the barcode into a line of words
    this.clearSuggestions();
    void this.searchFoodsAsync(code);
  }

  scanBarcode() {
    void this.navCtrl.pushAsync(IBarcodeScannerPage, { returnCallback: 'onBarcodeScannedAsync' });
  }

  /**
   * The floating "+ New" button (#5521). Create Food, Create Recipe and Add Macros used to be a band
   * of large buttons above the list; they are used far less than search, so they now sit behind one
   * button that opens a short menu (a bottom sheet on phones). Each row calls the same handler the
   * old button did, so what each action does is unchanged. The two events show whether moving them
   * behind a menu lowers their use.
   */
  handleNewButtonClick(ev: Event) {
    this.telemetry.trackEvent('foods_new_menu_open', {});
    const pick = (action: 'createFood' | 'createRecipe' | 'addMacros', run: () => void) => () => {
      this.telemetry.trackEvent('foods_new_menu_pick', { action });
      run();
    };
    const buttons: PopupMenuButton[] = [
      {
        text: LL.foods.buttons.createFood(),
        icon: foodTypeIcons[FoodType.Food].name,
        handler: pick('createFood', () => this.handleCreateFood()),
      },
      {
        text: LL.foods.buttons.createRecipe(),
        icon: foodTypeIcons[FoodType.Recipe].name,
        handler: pick('createRecipe', () => void this.handleCreateRecipe()),
      },
      {
        text: LL.foods.buttons.addMacros(),
        icon: 'add',
        handler: pick('addMacros', () => void this.handleAddMacros()),
      },
    ];

    void this.popupCtrl.createMenu({
      buttons: buttons,
      targetFromElement: ev.currentTarget as HTMLElement,
      horizontalAlign: 'right',
    });
  }


  handleSearchTextCopyToClipboard() {
    if (window.navigator['clipboard']) {
      void window.navigator['clipboard'].writeText(this.searchText);
      this.toastCtrl.showToast({
        message: LL.foods.messages.copiedToClipboard(),
        showCloseButton: false,
        duration: 2000,
      });
    }
  }

  async handleAddMacros() {
    // if in select mode, return result via callback
    if (this.mode === 'select' && this.navParams.data.returnCallback) {
      const result: FoodsPageResult = { action: 'addMacros' };
      if (this.navParams.data.keepOpenAfterSelect) {
        // same as picking a food: the screen for typing the macros in opens on TOP of this
        // list, and finishing there comes back here (#4967)
        await this.navCtrl.deliverResultWithoutPoppingAsync(
          this.navParams.data.returnCallback,
          this.navParams.data.local?.returnContext,
          result,
        );
      } else {
        await this.navCtrl.popAsync(result);
      }
      return;
    }

    // otherwise navigate to EditIngredientPage with custom macro ingredient.
    // log to the day the list was opened for, the same day tapping a food uses (#5502); a list
    // opened only for browsing carries no day, and then today is the right answer
    const dayDate = this.navParams.data.gotoLogFoodAfterSelect_dayDate
      ?? this.debug.getNowLocal().toISODate();
    const macroIngredient: IngredientMacro = {
      type: IngredientType.Custom,
      name: '',
      weight_g: 100,
      nutrients: {},
      status: FoodStatus.Custom,
    };
    void this.navCtrl.pushAsync(IEditIngredientPage, buildLogPortionScreenParams({
      dayDate,
      pendingIngredient: macroIngredient,
    }));
  }


  /** one "Show only" row of the Filters menu, which turns its filter on or off as it is tapped */
  private filterMenuToggle(name: FoodFilterName): PopupMenuButton {
    return {
      text: this.filterLabel(name),
      value: name,
      selectState: this.filters[name],
      controlType: 'toggle',
      handler: () => this.setFilter(name, !this.filters[name], 'menu'),
    };
  }

  /** the dialog's Reset: back to no filters at all. The values-by setting is not a filter and stays. */
  protected resetFilters() {
    // The dialog decides whether to offer Reset when it opens, so the user can switch every filter
    // off inside it and still tap Reset. There is then nothing to reset, and the event must not
    // report a reset of nothing.
    const count = this.activeFilterCount;
    if (count === 0) {
      return;
    }
    this.telemetry.trackEvent('foods_filter_reset', { count });
    this.changeFilters({ ...DEFAULT_FOODS_FILTER_STATE });
  }

  async handleFiltersButtonClick() {
    await this.popupCtrl.createMenu({
      title: LL.foods.filtersAndSettings(),
      selectMultiple: true,
      // offered only when there is something to reset
      headerAction: this.activeFilterCount > 0
        ? { text: LL.foods.filters.reset(), handler: () => this.resetFilters() }
        : undefined,
      subsections: [
        {
          header: LL.foods.filters.type(),
          buttons: this.typeFilterList.map(type => ({
            text: this.typeFilterLabel(type),
            value: type,
            selectState: this.filters.type === type,
            controlType: 'radio' as const,
            handler: () => this.setTypeFilter(type),
          })),
        },
        {
          header: LL.foods.filters.showOnly(),
          buttons: [
            this.filterMenuToggle('myFoods'),
            this.filterMenuToggle('favorites'),
            this.filterMenuToggle('loggedBefore'),
          ],
        },
        {
          header: LL.foods.filters.source(),
          buttons: [
            {
              ...this.filterMenuToggle('hideUnverified'),
              // the cards say "Community" and "Open Food Facts", so this quiet line under the
              // switch is where the app explains that those two are what "unverified" means
              description: LL.foods.filters.hideUnverifiedHint(),
            },
          ],
        },
        {
          header: LL.foods.showFoodValuesBy(),
          buttons: [
            {
              text: LL.foods.servingSize(),
              value: FoodValuesDisplayMode.Serving,
              selectState: this.settings.foodValuesDisplayMode === FoodValuesDisplayMode.Serving,
              controlType: 'radio',
              handler: () => {
                this.settings.foodValuesDisplayMode = FoodValuesDisplayMode.Serving;
                this.settings.saveInBackground('food values display mode');
                // the search reads this setting, so one still running has the old mode
                this.restartSearch();
              }
            },
            {
              text: LL.foods.per100g(),
              value: FoodValuesDisplayMode.Per100g,
              selectState: this.settings.foodValuesDisplayMode === FoodValuesDisplayMode.Per100g,
              controlType: 'radio',
              handler: () => {
                this.settings.foodValuesDisplayMode = FoodValuesDisplayMode.Per100g;
                this.settings.saveInBackground('food values display mode');
                // the search reads this setting, so one still running has the old mode
                this.restartSearch();
              }
            }
          ]
        }
      ],
      // A centred dialog on a large screen rather than a dropdown hung off the Filters chip:
      // this menu is a page of settings in four sections, and under a chip at the end of a row it
      // read as a dropdown that had outgrown its button. A phone keeps the bottom sheet.
      centered: true,
    });

    // every row applies as it is tapped, so there is nothing to read back off the closed menu
    this.cd.markForCheck();
  }

  // Serving info helpers - return raw values for food-card to format

  private shouldShowPer100g(food: FoodForUI): boolean {
    // "per 100g" setting applies to foods and recipes, but not meals
    return this.settings.foodValuesDisplayMode === FoodValuesDisplayMode.Per100g && !isLogMeal(food.cachedFood);
  }

  private getFoodServingMeasure(food: FoodForUI): FoodMeasure | undefined {
    if (!food.cachedFood || isLogMeal(food.cachedFood)) {
      return undefined;
    }
    const cachedFood = food.cachedFood as Food;
    // for recipes, describe ONE serving. The card pairs this amount with getFoodWeight's
    // weight_g and with nutrients scaled to that same weight, so the amount stays 1
    // and the weight is the recipe total divided by however many servings it makes. Passing
    // the recipe's whole weight here made the card show the total as if it were one serving,
    // ignoring a servings count the user had set (#4380).
    //
    // EVERY recipe takes this path, including one with no total weight. Such a recipe used to
    // fall through to the line below and get its own stored measure back, whose `amount` is how
    // many servings the whole recipe makes -- so the card read "20 serving" next to the weight of
    // a single one (deferred finding df-5a9d776905f9). buildRecipeServingMeasure handles a
    // missing total itself, so there is nothing to guard here.
    if (isRecipe(cachedFood)) {
      return buildRecipeServingMeasure(cachedFood.totalWeight_g, cachedFood.measures);
    }
    const measures = cachedFood.measures;
    return measures?.find(m => m.name === 'serving') || measures?.[0];
  }

  // returns the serving unit name, or undefined if showing per 100g or if unit is just 'serving'
  getFoodServingUnit(food: FoodForUI): string | undefined {
    if (this.shouldShowPer100g(food)) return undefined;
    // log meals always show "meal" as unit since they're not normalized to 100g
    if (food.cachedFood && isLogMeal(food.cachedFood)) return 'meal';
    const measure = this.getFoodServingMeasure(food);
    // don't show 'serving' as a unit name - it's not descriptive
    return measure?.name && measure.name !== 'serving' ? measure.name : undefined;
  }

  // returns the serving amount, or undefined if showing per 100g
  getFoodServingAmount(food: FoodForUI): number | undefined {
    if (this.shouldShowPer100g(food)) return undefined;
    // log meals always show "1" as amount
    if (food.cachedFood && isLogMeal(food.cachedFood)) return 1;
    const measure = this.getFoodServingMeasure(food);
    return measure?.amount;
  }

  // returns the weight to display
  getFoodWeight(food: FoodForUI): number | undefined {
    // log meals don't show weight
    if (food.cachedFood && isLogMeal(food.cachedFood)) {
      return undefined;
    }
    if (this.shouldShowPer100g(food)) return 100;
    const measure = this.getFoodServingMeasure(food);
    // the whole measure, since the card shows it beside the measure's own amount ("4 pc | 2.5 g"),
    // the same serving the food editor shows (design-docs/food-editor-nutrient-inputs.md)
    if (measure && measure.weight_g) {
      return measure.weight_g;
    }
    // fallback to 100g if no serving info
    return 100;
  }

  // returns display nutrients scaled to the current display mode (per-100g or per-serving)
  getFoodDisplayNutrients(food: FoodForUI): UiDisplayNutrient[] {
    // if no per-100g nutrients stored (e.g. log meals), use pre-computed displayNutrients
    if (!food.nutrientsPer100g) {
      return uiDisplayNutrients(food.displayNutrients);
    }
    // if showing per-100g, no scaling needed
    if (this.shouldShowPer100g(food)) {
      return FormatNutrients(food.nutrientsPer100g, this.userDb);
    }
    // scale to serving size
    const servingWeight = this.getFoodWeight(food) || 100;
    return FormatNutrients(food.nutrientsPer100g, this.userDb, { actual_g: servingWeight });
  }

  /**
   * The food card's ⋮ menu (#5520). It opens the food's details, which used to be two taps away
   * (tap the food, then View Details on the portion screen). Report is no longer offered here: it
   * lives on the details screen, under `canReportFood`, so every card gets this menu.
   */
  async handleFoodMenu(event: { event: Event; food: FoodHeader }) {
    const buttons: PopupMenuButton[] = [
      {
        text: LL.editIngredient.viewDetails(),
        icon: 'information-circle',
        handler: () => void this.openFoodDetailsAsync(event.food),
      },
    ];
    // Taking one food out of the history (owner, 2026-09-20). Offered only for a food that is in
    // the history and is not already out of it — see canForgetFoodFromHistory for who qualifies.
    if (canForgetFoodFromHistory(event.food.cachedFood)) {
      buttons.push({
        // the same clock the "Logged Before" badge uses, because this is that badge's history
        text: LL.foods.buttons.removeFromHistory(),
        icon: 'time',
        handler: () => void this.forgetFoodFromHistoryAsync(event.food),
      });
    }
    await this.popupCtrl.createMenu({
      targetFromElement: event.event.target as HTMLElement,
      buttons,
    });
  }

  /**
   * Take one food out of the user's history, from the card menu (owner, 2026-09-20).
   *
   * The food is NOT deleted and NOT un-cached. Its last-used date is stamped back to
   * `FOOD_FORGOTTEN_LAST_USED_UTC`, far enough in the past that the search ranking puts it below
   * every food the user really has eaten. So the food stays on the device, a search still finds
   * it, it still works offline — it just stops taking a place at the top of the list this page
   * shows before anything has been typed. That constant's own comment says why the date is
   * stamped rather than removed.
   */
  private async forgetFoodFromHistoryAsync(food: FoodHeader) {
    try {
      const cached = await this.userDb.getUsedFoodByIdAsync(food._id);
      if (!cached) {
        // the menu offered this because the listed card carried a cached food, so not finding one
        // now means the two disagree. Nothing is stamped and the list is left alone; report it,
        // because otherwise the user taps a menu item and silently nothing happens.
        this.logging.trackSilentProblem('foods.forget-history-food-not-cached', { foodId: food._id });
        return;
      }
      cached.local = cached.local || {};
      cached.local.lastUsed_utc = FOOD_FORGOTTEN_LAST_USED_UTC;
      // 'cached', never 'used': saving as used would stamp TODAY over the date we just set
      await this.foodDb.saveUserFoodAsync(cached, 'cached');
      this.toastCtrl.showToast({
        message: LL.foods.messages.removedFromHistory(),
        showCloseButton: false,
        duration: 2000,
      });
      // the list on screen is ordered by the very date that just changed, so re-run the search
      this.restartSearch();
    } catch (err) {
      // a failed write leaves the food where it was, which is safe, but the user asked for
      // something and did not get it — so it must not be swallowed
      this.logging.trackAbnormalException(err);
    }
  }

  /** Open a food's details screen read-only, or the meal screen for a logged meal. */
  private async openFoodDetailsAsync(food: FoodHeader) {
    // if it is a logged meal, open the meal -- what tapping one does when browsing
    if (isLogMealById(food._id)) {
      await this.navCtrl.pushAsync(IEditMealPage, { id: food._id });
      return;
    }
    await this.navCtrl.pushAsync(IEditFoodPage, {
      id: food._id,
      mode: 'view',
      // a list adding a food somewhere gets the food back from Log and finishes that errand;
      // a list opened for browsing lets Log log to today, as it always has
      logReturnsToCaller: this.hasAddFoodIntent(),
      returnCallback: 'onFoodDetailsClosed',
    });
  }

  /**
   * Answer from a details screen the card menu opened (#5520).
   *
   * - **Log:** pick the food exactly as a tap would, through the same guarded call, so the
   *   errand ends the same way.
   * - **A saved edit** (a bare id): re-run the search on screen so the card shows what was saved.
   *   Editing an Official food saves a private copy under a new id, which only a new search finds.
   * - **Back without saving:** nothing.
   */
  onFoodDetailsClosed(_context: unknown, result: FoodDetailsResult | string | undefined) {
    if (!result) return;
    if (typeof result === 'string') {
      // a plain search, not a restart: every save publishes `FoodUpdatedEvent` before the
      // details screen closes, and that already restarted the search. So a search for this text
      // still running now was started after the save and has the saved food.
      void this.searchFoodsAsync(this.searchText);
      return;
    }
    if (result.exit === FoodDetailsExit.Log) {
      this.selectionAccepted = true;
      void this.pickFoodUnderGuardAsync(result.foodId);
    }
  }
}
